import React, { useState, useEffect, useMemo } from 'react';
import { 
  Users, Utensils, ClipboardList, FileText, Printer, Plus, CheckCircle, CheckCircle2,
  AlertCircle, Search, ArrowUpDown, Trash2, Eye, EyeOff, X, Copy, ClipboardCheck, ShieldCheck, Mail, Edit, Save,
  Calendar, ChevronLeft, ChevronRight, Check, History, RefreshCw,
  CalendarDays, Upload, Link as LinkIcon, Loader2, Coins, Palmtree, ListChecks, Plane, PlusCircle, CalendarPlus
} from 'lucide-react';

import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, onSnapshot, addDoc, serverTimestamp, deleteDoc, doc, updateDoc, setDoc, getDoc, getDocs } from 'firebase/firestore';

/* global __firebase_config, __app_id, __initial_auth_token */

const GOOGLE_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbxlJYAqvZsd9DFN8-8e9c-QOJCPMcC6tPZE8fuV_mMxJDKiCF0o2WI1lM-5qIRlIt1U/exec";

// 1. GUESTS DATABASE CONFIG
let firebaseConfig = {};
if (typeof __firebase_config !== 'undefined') {
  firebaseConfig = JSON.parse(__firebase_config);
} else {
  firebaseConfig = {
    apiKey: "AIzaSyCMV1tV_ejsroZgXmlmvPANWtWCLR7jvE4",
    authDomain: "hh-pre-arrival-form.firebaseapp.com",
    projectId: "hh-pre-arrival-form",
    storageBucket: "hh-pre-arrival-form.firebasestorage.app",
    messagingSenderId: "179491588484",
    appId: "1:179491588484:web:c488196f97d7f219e835d1"
  };
}

const appGuest = initializeApp(firebaseConfig, "guestApp");
const authGuest = getAuth(appGuest);
const dbGuest = getFirestore(appGuest);
const isSandbox = typeof __app_id !== 'undefined';

const getGuestsCollection = () => {
  if (isSandbox) {
    const safeAppId = String(__app_id).replace(/\//g, '_');
    return collection(dbGuest, 'artifacts', safeAppId, 'public', 'data', 'guests');
  }
  return collection(dbGuest, 'guests');
};

const getGuestDoc = (id) => {
  if (isSandbox) {
    const safeAppId = String(__app_id).replace(/\//g, '_');
    return doc(dbGuest, 'artifacts', safeAppId, 'public', 'data', 'guests', id);
  }
  return doc(dbGuest, 'guests', id);
};

// 2. ROTA & OCCUPANCY DATABASE CONFIG
const rotaFirebaseConfig = {
  apiKey: "AIzaSyCw6hujj4XCUvsKXJ24WzJFlCHQgb4_o-4",
  authDomain: "hacienda-horses-rota.firebaseapp.com",
  projectId: "hacienda-horses-rota",
  storageBucket: "hacienda-horses-rota.firebasestorage.app",
  messagingSenderId: "187491376382",
  appId: "1:187491376382:web:b982620bcfd7d24837d9af"
};

const appRota = initializeApp(rotaFirebaseConfig, "rotaApp");
const authRota = getAuth(appRota);
const dbRota = getFirestore(appRota);

const ROTA_APP_ID = 'hacienda-rota-v1';
const OCCUPANCY_APP_ID = 'hacienda-occupancy-v1';

const ROLES = ['General Manager', 'Supervisor', 'Kitchen', 'FOH', 'Stables', 'Maintenance', 'Cleaning'];

const INITIAL_STAFF = [
  { id: 1, name: 'Vanessa', role: 'Maintenance', tipsMultiplier: 1, annualDays: 30, startDate: '2020-01-01' },
  { id: 2, name: 'Adrian', role: 'Supervisor', tipsMultiplier: 1, annualDays: 30, startDate: '2020-01-01' },
  { id: 3, name: 'Donna', role: 'FOH', tipsMultiplier: 0.5, annualDays: 30, startDate: '2020-01-01' },
  { id: 4, name: 'Steve', role: 'Kitchen', tipsMultiplier: 1, annualDays: 30, startDate: '2020-01-01' },
  { id: 5, name: 'AJ', role: 'Kitchen', tipsMultiplier: 1, annualDays: 30, startDate: '2020-01-01' },
  { id: 6, name: 'Mike', role: 'Stables', tipsMultiplier: 1, annualDays: 30, startDate: '2020-01-01' },
  { id: 7, name: 'Nicolas', role: 'Stables', tipsMultiplier: 1, annualDays: 30, startDate: '2020-01-01' },
  { id: 8, name: 'Jorge', role: 'Stables', tipsMultiplier: 1, annualDays: 30, startDate: '2020-01-01' },
  { id: 9, name: 'Alvaro', role: 'Stables', tipsMultiplier: 1, annualDays: 30, startDate: '2020-01-01' },
  { id: 10, name: 'Javi', role: 'Maintenance', tipsMultiplier: 0, annualDays: 30, startDate: '2020-01-01' }
];

const KNOWN_ROOMS = ["Olivers Room", "Rosie's Room", "Swallows", "The Dairy", "Tower Suite", "Sunset View", "Master Bedroom Main House", "West Wing Apartment", "Willows Room"];

const formatUKDate = (dateString) => {
  if (!dateString) return '';
  const parts = dateString.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateString;
};

// Dietary Summary Generator (Lightweight NLP)
const generateDietarySummary = (text) => {
  if (!text) return "Special Diet";
  const lower = text.toLowerCase();
  const tags = [];
  
  if (lower.includes('gluten') || lower.includes('coeliac') || lower.includes('celiac') || lower.includes('wheat')) tags.push("Gluten-Free");
  if (lower.includes('veg') && !lower.includes('vegan')) tags.push("Vegetarian");
  if (lower.includes('vegan')) tags.push("Vegan");
  if (lower.includes('dairy') || lower.includes('lactose') || lower.includes('milk') || lower.includes('cheese')) tags.push("Dairy-Free");
  if (lower.includes('nut ') || lower.includes('nuts') || lower.includes('peanut')) tags.push("Nuts");
  if (lower.includes('fish') || lower.includes('seafood') || lower.includes('prawn') || lower.includes('shrimp')) tags.push("No Seafood");
  if (lower.includes('meat') || lower.includes('beef') || lower.includes('pork') || lower.includes('chicken')) tags.push("Meat Restr.");
  if (lower.includes('allerg')) tags.push("Allergy");

  if (tags.length > 0) return tags.slice(0, 2).join(", ") + (tags.length > 2 ? "..." : "");
  return "See Notes";
};

// Fuzzy match algorithm for Form Tracker
const getMatchingForms = (event, allGuests) => {
  const eventGuestRaw = (event.guest || '').toLowerCase();
  const eventGuestNorm = eventGuestRaw.replace(/[^a-z0-9]/g, '');
  const eventWords = eventGuestRaw.replace(/[^a-z0-9]/g, ' ').split(/\s+/).filter(w => w.length > 2);

  return allGuests.filter(g => {
    if (!g.name && !g.bookingHolder) return false;

    // --- EXPIRY CHECK FOR RETURNING GUESTS ---
    if (g.arrivalDate) {
       const gDate = new Date(g.arrivalDate);
       if (!isNaN(gDate)) {
           const today = new Date();
           today.setHours(0,0,0,0);
           const daysSinceFormArrival = Math.ceil((today - gDate) / (1000 * 60 * 60 * 24));
           if (daysSinceFormArrival > 30) {
               return false; // Automatically reject forms older than 30 days
           }
       }
    }

    const nameRaw = (g.name || '').toLowerCase();
    const holderRaw = (g.bookingHolder || '').toLowerCase();

    const nameNorm = nameRaw.replace(/[^a-z0-9]/g, '');
    const bookingHolderNorm = holderRaw.replace(/[^a-z0-9]/g, '');

    let nameMatch = false;
    let isStrongNameMatch = false;

    // 1. Direct string match
    if (eventGuestNorm.length > 3) {
      if (bookingHolderNorm && (eventGuestNorm.includes(bookingHolderNorm) || bookingHolderNorm.includes(eventGuestNorm))) {
        nameMatch = true;
        if (eventGuestNorm === bookingHolderNorm) isStrongNameMatch = true;
      }
      if (nameNorm && (eventGuestNorm.includes(nameNorm) || nameNorm.includes(eventGuestNorm))) {
        nameMatch = true;
        if (eventGuestNorm === nameNorm) isStrongNameMatch = true;
      }
    }

    // 2. Word Intersection Match
    if (!nameMatch && eventWords.length > 0) {
      const nameWords = nameRaw.replace(/[^a-z0-9]/g, ' ').split(/\s+/).filter(w => w.length > 2);
      const holderWords = holderRaw.replace(/[^a-z0-9]/g, ' ').split(/\s+/).filter(w => w.length > 2);

      let matchCount = 0;
      for (const word of eventWords) {
        if (nameWords.includes(word) || holderWords.includes(word)) {
          nameMatch = true;
          matchCount++;
        }
      }
      if (matchCount >= 2) isStrongNameMatch = true;
    }

    // 3. Smart Date Match
    let dateMatch = false;
    if (g.arrivalDate) {
       const gDate = new Date(g.arrivalDate);
       const eDate = event.startDate;
       if (!isNaN(gDate) && !isNaN(eDate)) {
           const diffTime = Math.abs(eDate - gDate);
           const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
           if (diffDays <= 7 || isStrongNameMatch) {
               dateMatch = true;
           }
       } else {
           dateMatch = true;
       }
    } else {
       dateMatch = true;
    }

    return nameMatch && dateMatch;
  });
};

const getAbilitiesList = (guest) => {
  if (guest.abilities && typeof guest.abilities === 'object') {
    return Object.entries(guest.abilities)
      .filter(([_, isChecked]) => isChecked)
      .map(([key, _]) => key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim());
  }
  if (guest.abilitiesText) return guest.abilitiesText.split(',').map(s => s.trim()).filter(Boolean);
  if (typeof guest.abilities === 'string') return guest.abilities.split(',').map(s => s.trim()).filter(Boolean);
  return [];
};

const checkIsNonRider = (guest) => {
  if (guest.abilities?.nonRider) return true;
  const textToScan = ((guest.experience || '') + ' ' + (guest.abilitiesText || '')).toLowerCase();
  if (textToScan.includes('non rider') || textToScan.includes('non-rider') || textToScan.includes('non riding') || textToScan.includes('not riding')) {
      return true;
  }
  return false;
};

const getStartOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setHours(0, 0, 0, 0);
  return new Date(d.setDate(diff));
};

const formatDateKey = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const parseSummary = (summary) => {
  const parts = summary.split(' - ');
  if (parts.length >= 3) return { room: parts[1].trim(), guest: parts.slice(2).join(' - ').trim() };
  if (parts.length === 2) return { room: parts[0].trim(), guest: parts[1].trim() };
  return { room: "Unknown Room", guest: summary };
};

const parseOccupancy = (desc, summary) => {
  let total = 0;
  const match = desc.match(/(\d+)\s*(?:Adult|Guest|Person)/i);
  if (match) total += parseInt(match[1], 10);
  const childMatch = desc.match(/(\d+)\s*Child/i);
  if (childMatch) total += parseInt(childMatch[1], 10);
  if (total === 0 && summary) {
      const titleMatch = summary.match(/\((\d+)\)/);
      if (titleMatch) total = parseInt(titleMatch[1], 10);
  }
  return total > 0 ? total : 2; 
};

const Sidebar = ({ currentView, setCurrentView }) => (
  <div className="w-64 bg-[#162635] text-[#F0F8F8] min-h-screen flex flex-col print:hidden shadow-2xl z-10 relative overflow-y-auto">
    <div className="p-6 border-b border-white/10 sticky top-0 bg-[#162635] z-20">
      <h1 className="text-2xl font-serif font-bold text-white mb-1 tracking-wide">Hacienda Horses</h1>
      <p className="text-[10px] text-[#28CFD4] font-bold uppercase tracking-widest">Unified Dashboard</p>
    </div>
    
    <div className="flex-1 px-4 py-6 space-y-8">
      {/* GUESTS */}
      <div className="space-y-1.5">
         <p className="px-4 text-[10px] font-bold text-white/50 uppercase tracking-widest mb-3">Guests</p>
         <button onClick={() => setCurrentView('dashboard')} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${currentView === 'dashboard' ? 'bg-[#28CFD4] text-white shadow-md' : 'hover:bg-white/10 text-stone-300'}`}>
           <Users size={18} /> <span className="font-medium text-sm">All Guests</span>
         </button>
         <button onClick={() => setCurrentView('form')} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${currentView === 'form' ? 'bg-[#28CFD4] text-white shadow-md' : 'hover:bg-white/10 text-stone-300'}`}>
           <Plus size={18} /> <span className="font-medium text-sm">New Registration</span>
         </button>
         <button onClick={() => setCurrentView('tracker')} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${currentView === 'tracker' ? 'bg-amber-500 text-white shadow-md' : 'hover:bg-white/10 text-stone-300'}`}>
           <ListChecks size={18} /> <span className="font-medium text-sm">Form Tracker</span>
         </button>
      </div>

      {/* OPERATIONS */}
      <div className="space-y-1.5">
         <p className="px-4 text-[10px] font-bold text-white/50 uppercase tracking-widest mb-3">Operations</p>
         <button onClick={() => setCurrentView('arrivals')} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${currentView === 'arrivals' ? 'bg-[#28CFD4] text-white shadow-md' : 'hover:bg-white/10 text-stone-300'}`}>
           <Plane size={18} /> <span className="font-medium text-sm">Arrivals & Departures</span>
         </button>
         <button onClick={() => setCurrentView('kitchenCalendar')} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${currentView === 'kitchenCalendar' ? 'bg-orange-500 text-white shadow-md' : 'hover:bg-white/10 text-stone-300'}`}>
           <CalendarDays size={18} /> <span className="font-medium text-sm">Kitchen Calendar</span>
         </button>
         <button onClick={() => setCurrentView('kitchen')} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${currentView === 'kitchen' ? 'bg-[#28CFD4] text-white shadow-md' : 'hover:bg-white/10 text-stone-300'}`}>
           <Utensils size={18} /> <span className="font-medium text-sm">Kitchen Sheet</span>
         </button>
         <button onClick={() => setCurrentView('stables')} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${currentView === 'stables' ? 'bg-[#28CFD4] text-white shadow-md' : 'hover:bg-white/10 text-stone-300'}`}>
           <ClipboardList size={18} /> <span className="font-medium text-sm">Stables Sheet</span>
         </button>
         <button onClick={() => setCurrentView('assessments')} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${currentView === 'assessments' ? 'bg-[#28CFD4] text-white shadow-md' : 'hover:bg-white/10 text-stone-300'}`}>
           <ClipboardCheck size={18} /> <span className="font-medium text-sm">Assessments</span>
         </button>
         <button onClick={() => setCurrentView('insurance')} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${currentView === 'insurance' ? 'bg-[#28CFD4] text-white shadow-md' : 'hover:bg-white/10 text-stone-300'}`}>
           <ShieldCheck size={18} /> <span className="font-medium text-sm">Insurance Forms</span>
         </button>
      </div>

      {/* MANAGEMENT */}
      <div className="space-y-1.5">
         <p className="px-4 text-[10px] font-bold text-white/50 uppercase tracking-widest mb-3">Management</p>
         <button onClick={() => setCurrentView('occupancy')} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${currentView === 'occupancy' ? 'bg-cyan-600 text-white shadow-md' : 'hover:bg-white/10 text-stone-300'}`}>
           <CalendarDays size={18} /> <span className="font-medium text-sm">Occupancy</span>
         </button>
         <button onClick={() => setCurrentView('rota')} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${currentView === 'rota' ? 'bg-cyan-600 text-white shadow-md' : 'hover:bg-white/10 text-stone-300'}`}>
           <Calendar size={18} /> <span className="font-medium text-sm">Staff Rota</span>
         </button>
         <button onClick={() => setCurrentView('staff')} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${currentView === 'staff' ? 'bg-cyan-600 text-white shadow-md' : 'hover:bg-white/10 text-stone-300'}`}>
           <Users size={18} /> <span className="font-medium text-sm">Team & Holidays</span>
         </button>
         <button onClick={() => setCurrentView('tips')} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${currentView === 'tips' ? 'bg-yellow-600 text-white shadow-md' : 'hover:bg-white/10 text-stone-300'}`}>
           <Coins size={18} /> <span className="font-medium text-sm">Tips Calculator</span>
         </button>
      </div>
    </div>
  </div>
);

const GlobalToolbar = ({ currentView, displayGuestsLength, searchTerm, setSearchTerm, filterMonth, setFilterMonth, specificDate, setSpecificDate, months, sortBy, setSortBy, sortOrder, setSortOrder, handleFindDuplicates, handleCopyEmails }) => (
  <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 mb-8 print:hidden">
    <div>
      <h2 className="text-4xl font-serif font-bold text-[#162635] capitalize">
        {currentView === 'dashboard' ? 'Master Guest List' : currentView === 'tracker' ? 'Form Tracker' : `${currentView} Sheet`}
      </h2>
      <p className="text-stone-500 font-medium text-sm mt-1">Viewing <span className="text-[#28CFD4] font-bold">{displayGuestsLength}</span> records</p>
    </div>
    <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto">
      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
        <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-2.5 bg-white border border-stone-200 rounded-lg focus:ring-2 focus:ring-[#28CFD4] outline-none shadow-sm" />
      </div>
      <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="px-4 py-2.5 bg-white border border-stone-200 rounded-lg focus:ring-2 focus:ring-[#28CFD4] outline-none shadow-sm font-medium text-stone-700 cursor-pointer">
        <option value="">All Dates</option>
        <option value="today" className="font-bold text-amber-500">Today</option>
        <option value="next7" className="font-bold text-[#28CFD4]">Next 7 Days</option>
        <option value="next30" className="font-bold text-[#28CFD4]">Next 30 Days</option>
        <option value="specific" className="font-bold text-purple-500">Specific Date...</option>
        {months.map(m => (
          <option key={m} value={m}>{new Date(m + '-01').toLocaleString('default', { month: 'long', year: 'numeric' })}</option>
        ))}
      </select>
      
      {filterMonth === 'specific' && (
         <input type="date" value={specificDate} onChange={(e) => setSpecificDate(e.target.value)} className="px-4 py-2.5 bg-white border border-stone-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none shadow-sm font-medium text-stone-700" />
      )}

      <div className="flex items-center bg-white border border-stone-200 rounded-lg p-1 shadow-sm">
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="bg-transparent text-sm font-medium text-stone-700 outline-none px-3 py-1.5 cursor-pointer appearance-none">
          <option value="arrivalDate">Sort by Date</option>
          <option value="name">Sort by Name</option>
        </select>
        <div className="w-px h-6 bg-stone-200 mx-1"></div>
        <button onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')} className="p-1.5 text-stone-400 hover:text-[#28CFD4] transition-colors rounded hover:bg-stone-50 flex items-center gap-1" title="Toggle Order">
          <ArrowUpDown size={16} /> <span className="text-xs font-medium uppercase w-8 text-center">{sortOrder}</span>
        </button>
      </div>
      {currentView === 'dashboard' && (
        <div className="flex items-center gap-2">
          <button onClick={handleCopyEmails} className="flex items-center gap-2 px-5 py-2.5 bg-white border border-stone-200 hover:bg-stone-50 text-stone-700 rounded-lg transition-colors font-bold shadow-sm" title="Copy visible emails">
            <Mail size={18} /> Copy Emails
          </button>
          <button onClick={handleFindDuplicates} className="flex items-center gap-2 px-5 py-2.5 bg-white border border-stone-200 hover:bg-stone-50 text-stone-700 rounded-lg transition-colors font-bold shadow-sm">
            <Copy size={18} /> Find Duplicates
          </button>
        </div>
      )}
      {currentView !== 'dashboard' && currentView !== 'tracker' && (
        <button onClick={() => window.print()} className="flex items-center gap-2 px-5 py-2.5 bg-[#162635] hover:bg-[#28CFD4] text-white rounded-lg transition-colors font-bold shadow-sm">
          <Printer size={18} /> Print
        </button>
      )}
    </div>
  </div>
);

const DashboardView = ({ displayGuests, setSelectedGuest, handleDelete }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="bg-[#F0F8F8] text-[#162635] text-xs border-b border-stone-200">
            <th className="p-5 font-bold uppercase tracking-wider">Name</th>
            <th className="p-5 font-bold uppercase tracking-wider">Arrival</th>
            <th className="p-5 font-bold uppercase tracking-wider">Departure</th>
            <th className="p-5 font-bold uppercase tracking-wider">Ability</th>
            <th className="p-5 font-bold uppercase tracking-wider">Needs</th>
            <th className="p-5 font-bold uppercase tracking-wider">Insurance</th>
            <th className="p-5 font-bold uppercase tracking-wider text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {displayGuests.length === 0 ? (
            <tr><td colSpan="7" className="p-12 text-center text-stone-500 font-medium">No matching guests found.</td></tr>
          ) : (
            displayGuests.map(guest => {
              const abilities = getAbilitiesList(guest);
              return (
                <tr key={guest.id} className="border-b border-stone-100 hover:bg-[#F0F8F8]/50 transition-colors">
                  <td className="p-5">
                    <div className="font-serif font-bold text-[#162635] text-lg">{guest.name}</div>
                    <div className="text-xs text-stone-500 font-medium">{guest.email}</div>
                  </td>
                  <td className="p-5">
                    <div className="text-[#162635] font-bold">{formatUKDate(guest.arrivalDate)} <span className="font-normal">{guest.arrivalTime}</span></div>
                    <div className="text-xs text-[#28CFD4] font-bold mt-0.5">{guest.arrivalFlightNo || guest.arrivalFlight}</div>
                  </td>
                  <td className="p-5">
                    <div className="text-[#162635] font-bold">{formatUKDate(guest.departureDate)} <span className="font-normal">{guest.departureTime}</span></div>
                    <div className="text-xs text-stone-400 font-bold mt-0.5">{guest.departureFlightNo || guest.departureFlight}</div>
                  </td>
                  <td className="p-5">
                    <div className="flex flex-wrap gap-1.5 max-w-[200px]">
                      {abilities.length > 0 ? abilities.map((ability, i) => (
                        <span key={i} className="inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold bg-[#F0F8F8] text-[#162635] border border-stone-100 whitespace-nowrap">{ability}</span>
                      )) : <span className="text-xs text-stone-400 italic">None logged</span>}
                    </div>
                  </td>
                  <td className="p-5">
                    {guest.hasDietary === 'Yes' || guest.hasMedical === 'Yes' || guest.hasNeeds === 'Yes' ? (
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 text-amber-600" title="Has special requirements"><AlertCircle size={18} /></div>
                    ) : (
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#F0F8F8] text-[#28CFD4]" title="No requirements"><CheckCircle size={18} /></div>
                    )}
                  </td>
                  <td className="p-5">
                    {guest.hasInsurance === 'No' ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-rose-50 text-rose-600 border border-rose-100">€10/day</span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-[#F0F8F8] text-[#28CFD4]">Covered</span>
                    )}
                  </td>
                  <td className="p-5 text-right whitespace-nowrap">
                    <button onClick={() => setSelectedGuest(guest)} className="p-2 text-stone-400 hover:text-[#28CFD4] hover:bg-[#F0F8F8] rounded-full transition-colors mr-1" title="View Full Details"><Eye size={18} /></button>
                    <button onClick={() => handleDelete(guest.id)} className="p-2 text-stone-300 hover:text-rose-600 hover:bg-rose-50 rounded-full transition-colors" title="Delete Guest"><Trash2 size={18} /></button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const FormTrackerView = ({ events, displayGuests, updateEventStatus, showAlert }) => {
  const handleEmailAction = async (ev, type, suggestedEmail) => {
    let email = ev.guestEmail || suggestedEmail;
    if (!email) {
      email = window.prompt(`Please enter the email address for ${ev.guest} (from InnStyle):`);
      if (!email) return; 
    }

    const isInitial = type === 'initial';
    
    const updates = { guestEmail: email };
    if (isInitial) {
        updates.preArrivalSent = true;
    } else {
        updates.reminderSent = true;
    }
    updateEventStatus(ev.id, updates);

    try {
        await fetch(GOOGLE_SCRIPT_URL, {
            method: 'POST',
            mode: 'no-cors',
            headers: { 'Content-Type': 'text/plain;charset=utf-8' },
            body: JSON.stringify({
                type: type,
                guestName: ev.guest,
                email: email,
                room: ev.room,
                arrivalDate: formatUKDate(formatDateKey(ev.startDate))
            })
        });
        showAlert(isInitial ? "Welcome email sent!" : "Reminder email sent!");
    } catch (e) {
        console.error("Email error:", e);
        showAlert("Data saved, but check your Google Apps Script URL if the email doesn't arrive.", "Warning");
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto pb-24 animate-in fade-in duration-300">
      <div className="mb-6 flex items-center gap-3 text-stone-600 bg-white p-4 rounded-xl shadow-sm border border-stone-100">
          <ListChecks className="w-6 h-6 text-amber-500" /> 
          <span className="font-medium">Cross-referencing your InnStyle Occupancy calendar with received Pre-Arrival forms.</span>
      </div>

      <div className="space-y-4">
        {events.map(ev => {
          const matchingForms = getMatchingForms(ev, displayGuests);
          const formsNeeded = ev.occupancy;
          const formsReceived = matchingForms.length;
          const isComplete = formsReceived >= formsNeeded;
          const isMissing = formsReceived > 0 && formsReceived < formsNeeded;
          
          const suggestedEmail = ev.guestEmail || (matchingForms.length > 0 ? matchingForms[0].email : "");

          return (
            <div key={ev.id} className={`bg-white border-l-4 rounded-xl shadow-sm p-6 flex flex-col justify-between gap-4 ${isComplete ? 'border-emerald-500' : isMissing ? 'border-amber-500' : 'border-rose-500'}`}>
              
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                  <div className="flex-1">
                    <h3 className="font-bold text-xl text-[#162635] flex items-center gap-2 mb-1">
                      {ev.guest} <span className="text-sm font-bold text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full">{ev.room}</span>
                      {ev.isManualBooking && <span className="text-xs font-bold text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full ml-2">Manual</span>}
                      {ev.isManualRoomOverride && <span className="text-[10px] font-bold text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded ml-1" title={`Moved from ${ev.originalRoom}`}>Moved</span>}
                    </h3>
                    <p className="text-sm text-stone-500 font-medium">
                        <span className="text-stone-400">Arriving:</span> {formatUKDate(formatDateKey(ev.startDate))} &nbsp;&nbsp;|&nbsp;&nbsp; 
                        <span className="text-stone-400">Departing:</span> {formatUKDate(formatDateKey(ev.endDate))}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-8 bg-stone-50 px-6 py-4 rounded-xl border border-stone-100">
                    <div className="text-center">
                      <div className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-1">Forms Status</div>
                      <div className={`font-bold text-2xl flex items-center justify-center gap-2 ${isComplete ? 'text-emerald-600' : isMissing ? 'text-amber-500' : 'text-rose-600'}`}>
                        {isComplete ? <CheckCircle2 className="w-5 h-5"/> : <AlertCircle className="w-5 h-5"/>}
                        {formsReceived} / {formsNeeded}
                      </div>
                    </div>
                    
                    {matchingForms.length > 0 && (
                      <div className="flex -space-x-2 pl-4 border-l border-stone-200">
                        {matchingForms.map(mf => (
                          <div key={mf.id} className="w-10 h-10 rounded-full bg-[#162635] border-2 border-white flex items-center justify-center text-xs font-bold text-[#28CFD4] shadow-sm" title={mf.name}>
                            {mf.name.substring(0,2).toUpperCase()}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
              </div>

              {!isComplete && (
                <div className="flex flex-col gap-3 mt-2 bg-stone-50 p-4 rounded-xl border border-stone-200">
                    <div className="flex flex-wrap items-center gap-6">
                        <label className="flex items-center gap-2 cursor-pointer group" title="Manually mark balance as paid">
                            <input type="checkbox" checked={!!ev.balancePaid} onChange={() => updateEventStatus(ev.id, { balancePaid: !ev.balancePaid })} className="w-5 h-5 text-emerald-600 rounded border-stone-300 focus:ring-emerald-500 cursor-pointer" />
                            <span className="text-sm font-bold text-stone-700 group-hover:text-emerald-700 transition-colors">Balance Paid</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer group" title="Manually mark form as sent">
                            <input type="checkbox" checked={!!ev.preArrivalSent} onChange={() => updateEventStatus(ev.id, { preArrivalSent: !ev.preArrivalSent })} className="w-5 h-5 text-emerald-600 rounded border-stone-300 focus:ring-emerald-500 cursor-pointer" />
                            <span className="text-sm font-bold text-stone-700 group-hover:text-emerald-700 transition-colors">Welcome Sent</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer group" title="Manually mark reminder as sent">
                            <input type="checkbox" checked={!!ev.reminderSent} onChange={() => updateEventStatus(ev.id, { reminderSent: !ev.reminderSent })} className="w-5 h-5 text-amber-500 rounded border-stone-300 focus:ring-amber-500 cursor-pointer" />
                            <span className="text-sm font-bold text-stone-700 group-hover:text-amber-600 transition-colors">Reminder Sent</span>
                        </label>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-stone-200">
                        <button 
                            disabled={!ev.balancePaid}
                            onClick={() => handleEmailAction(ev, 'initial', suggestedEmail)} 
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm transition shadow-sm ${ev.balancePaid ? (ev.preArrivalSent ? 'bg-emerald-100 text-emerald-700 border border-emerald-300 hover:bg-emerald-200' : 'bg-emerald-600 text-white hover:bg-emerald-700') : 'bg-stone-200 text-stone-400 cursor-not-allowed'}`}
                        >
                            <Mail size={16} /> {ev.preArrivalSent ? 'Resend Welcome Email' : 'Send Welcome Email'}
                        </button>

                        <button 
                            disabled={!ev.preArrivalSent}
                            onClick={() => handleEmailAction(ev, 'reminder', suggestedEmail)} 
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm transition shadow-sm ${ev.preArrivalSent ? (ev.reminderSent ? 'bg-amber-100 text-amber-700 border border-amber-300 hover:bg-amber-200' : 'bg-amber-500 text-white hover:bg-amber-600') : 'bg-stone-200 text-stone-400 cursor-not-allowed'}`}
                        >
                            <Mail size={16} /> {ev.reminderSent ? 'Resend Reminder' : 'Send Reminder Email'}
                        </button>
                    </div>
                </div>
              )}
            </div>
          );
        })}
        
        {events.length === 0 && (
          <div className="p-12 text-center bg-white rounded-2xl border border-stone-100 shadow-sm">
             <CalendarDays className="w-12 h-12 text-stone-300 mx-auto mb-4"/>
             <h3 className="text-xl font-bold text-stone-700 mb-2">No Upcoming Bookings Found</h3>
             <p className="text-stone-500">No bookings match your current search and date filters. (Ensure your Occupancy Calendar is synced!)</p>
          </div>
        )}
      </div>
    </div>
  );
};

const ArrivalsPrintSheet = ({ displayGuests, events }) => {
  const findRoom = (guest) => {
      const matchedEvent = events.find(ev => {
          const forms = getMatchingForms(ev, [guest]);
          return forms.length > 0;
      });
      return matchedEvent ? matchedEvent.room : 'TBA';
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden">
      <div className="hidden print:block p-8 pb-4 border-b border-stone-200">
          <h1 className="text-3xl font-serif font-bold text-[#162635]">Arrivals & Departures</h1>
          <p className="text-stone-500 mt-1 italic">Generated: {new Date().toLocaleDateString('en-GB')}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-[#F0F8F8] text-[#162635] text-xs border-b border-stone-200">
              <th className="p-4 font-bold uppercase tracking-wider border-r border-stone-200">Guest / Contact</th>
              <th className="p-4 font-bold uppercase tracking-wider border-r border-stone-200">Room</th>
              <th className="p-4 font-bold uppercase tracking-wider border-r border-stone-200">Arrival Details</th>
              <th className="p-4 font-bold uppercase tracking-wider">Departure Details</th>
            </tr>
          </thead>
          {displayGuests.length === 0 ? (
            <tbody>
              <tr><td colSpan="4" className="p-12 text-center text-stone-500 font-medium">No arrivals or departures matching current filters.</td></tr>
            </tbody>
          ) : (
            <tbody>
              {displayGuests.map((guest, idx) => {
                const bgClass = idx % 2 === 0 ? 'bg-white' : 'bg-stone-50/50';
                const roomName = findRoom(guest);
                return (
                  <tr key={guest.id} className={`border-b border-stone-100 break-inside-avoid print:break-inside-avoid ${bgClass}`}>
                    <td className="p-4 border-r border-stone-100 align-top">
                      <div className="font-bold text-[#162635] text-base">{guest.name}</div>
                      {guest.bookingHolder && guest.bookingHolder !== guest.name && <div className="text-xs text-stone-500 italic mt-0.5">Booking: {guest.bookingHolder}</div>}
                      {guest.phone && <div className="text-sm font-medium text-stone-600 mt-2">{guest.phone}</div>}
                    </td>
                    <td className="p-4 border-r border-stone-100 align-top">
                      <span className="inline-block px-2.5 py-1 bg-stone-100 text-stone-700 font-bold rounded text-sm">{roomName}</span>
                    </td>
                    <td className="p-4 border-r border-stone-100 text-sm align-top whitespace-nowrap">
                      <div className="font-bold text-[#162635] text-base">{formatUKDate(guest.arrivalDate)}</div>
                      <div className="text-stone-500 mt-0.5 font-medium">{guest.arrivalTime || 'Time TBA'}</div>
                      {(guest.arrivalFlightNo || guest.arrivalFlight) && <div className="text-xs font-bold text-[#28CFD4] mt-1">Flight: {guest.arrivalFlightNo || guest.arrivalFlight}</div>}
                      {guest.transfers === 'Yes' && <div className="mt-2 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-200">Transfer Req.</div>}
                    </td>
                    <td className="p-4 text-sm align-top whitespace-nowrap">
                      <div className="font-bold text-[#162635] text-base">{formatUKDate(guest.departureDate)}</div>
                      <div className="text-stone-500 mt-0.5 font-medium">{guest.departureTime || 'Time TBA'}</div>
                      {(guest.departureFlightNo || guest.departureFlight) && <div className="text-xs font-bold text-[#28CFD4] mt-1">Flight: {guest.departureFlightNo || guest.departureFlight}</div>}
                      {guest.transfers === 'Yes' && <div className="mt-2 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-200">Transfer Req.</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      </div>
    </div>
  );
};

const RegistrationForm = ({ setCurrentView, showAlert }) => {
  const [formData, setFormData] = useState({
    name: '', bookingHolder: '', email: '', phone: '',
    arrivalDate: '', arrivalTime: '', arrivalFlightNo: '', departureDate: '', departureTime: '', departureFlightNo: '', transfers: 'No',
    age: '', height: '', weight: '', experience: '',
    abilities: { nonRider: false, neverRidden: false, beginner: false, intermediate: false, advanced: false, hackingWalkTrot: false, hackingCanterGallop: false },
    hasDietary: 'No', dietaryDetails: '', hasMedical: 'No', medicalDetails: '', hasNeeds: 'No', needsDetails: '',
    emergencyContact: '', emergencyPhone: '', hasInsurance: 'Yes', insuranceCompany: '', policyNumber: '',
    photoConsent: 'Yes', termsAccepted: false, signatureName: '', dateSigned: new Date().toISOString().split('T')[0]
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('ability_')) {
      const abilityKey = name.split('_')[1];
      setFormData(prev => ({ ...prev, abilities: { ...prev.abilities, [abilityKey]: checked } }));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const guestsRef = getGuestsCollection();
      await addDoc(guestsRef, { ...formData, createdAt: serverTimestamp() });
      setSuccess(true);
      setTimeout(() => { setSuccess(false); setCurrentView('dashboard'); }, 2000);
    } catch (error) {
      console.error("Error saving guest:", error);
      if(showAlert) showAlert("There was an error saving the form.", "Error");
    }
    setSubmitting(false);
  };

  if (success) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
        <div className="w-24 h-24 bg-white text-[#28CFD4] rounded-full flex items-center justify-center mb-6 shadow-sm border border-stone-100"><CheckCircle size={48} /></div>
        <h2 className="text-4xl font-serif font-bold text-[#162635] mb-3">Registration Submitted</h2>
        <p className="text-stone-500 font-medium text-lg">Thank you. The guest details have been added.</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto pb-24">
      <div className="mb-8 border-b border-stone-200 pb-6">
        <h2 className="text-4xl font-serif font-bold text-[#162635] mb-2">Pre-Arrival Registration</h2>
        <p className="text-stone-600">Please complete this form for each rider/guest. This information helps us ensure you have a safe, comfortable, and enjoyable holiday.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <section className="bg-white p-8 rounded-2xl shadow-sm border border-stone-100">
          <h3 className="text-xl font-serif font-semibold text-[#162635] mb-6 flex items-center gap-3">
            <div className="p-2.5 bg-[#F0F8F8] rounded-full"><Users size={20} className="text-[#28CFD4]"/></div> Personal Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div><label className="block text-sm font-bold text-stone-700 mb-1.5">Full Name</label><input required type="text" name="name" value={formData.name} onChange={handleChange} className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#28CFD4] outline-none" /></div>
            <div><label className="block text-sm font-bold text-stone-700 mb-1.5">Booking Holder Name</label><input required type="text" name="bookingHolder" value={formData.bookingHolder} onChange={handleChange} className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#28CFD4] outline-none" /></div>
            <div><label className="block text-sm font-bold text-stone-700 mb-1.5">Email Address</label><input required type="email" name="email" value={formData.email} onChange={handleChange} className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#28CFD4] outline-none" /></div>
            <div><label className="block text-sm font-bold text-stone-700 mb-1.5">Phone Number</label><input required type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#28CFD4] outline-none" /></div>
          </div>
        </section>

        <section className="bg-white p-8 rounded-2xl shadow-sm border border-stone-100">
          <h3 className="text-xl font-serif font-semibold text-[#162635] mb-6 flex items-center gap-3">
            <div className="p-2.5 bg-[#F0F8F8] rounded-full"><FileText size={20} className="text-[#28CFD4]"/></div> Travel Arrangements
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div><label className="block text-sm font-bold text-stone-700 mb-1.5">Arrival Date</label><input required type="date" name="arrivalDate" value={formData.arrivalDate} onChange={handleChange} className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#28CFD4] outline-none" /></div>
            <div><label className="block text-sm font-bold text-stone-700 mb-1.5">Arrival Time</label><input type="time" name="arrivalTime" value={formData.arrivalTime} onChange={handleChange} className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#28CFD4] outline-none" /></div>
            <div><label className="block text-sm font-bold text-stone-700 mb-1.5">Arrival Flight No.</label><input type="text" name="arrivalFlightNo" value={formData.arrivalFlightNo} onChange={handleChange} className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#28CFD4] outline-none" /></div>
            <div><label className="block text-sm font-bold text-stone-700 mb-1.5">Departure Date</label><input required type="date" name="departureDate" value={formData.departureDate} onChange={handleChange} className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#28CFD4] outline-none" /></div>
            <div><label className="block text-sm font-bold text-stone-700 mb-1.5">Departure Time</label><input type="time" name="departureTime" value={formData.departureTime} onChange={handleChange} className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#28CFD4] outline-none" /></div>
            <div><label className="block text-sm font-bold text-stone-700 mb-1.5">Departure Flight No.</label><input type="text" name="departureFlightNo" value={formData.departureFlightNo} onChange={handleChange} className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#28CFD4] outline-none" /></div>
            <div className="md:col-span-3 mt-3">
              <label className="block text-sm font-bold text-stone-700 mb-3">Require airport transfers? (€95 each way)</label>
              <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer p-3 border border-stone-100 rounded-xl"><input type="radio" name="transfers" value="Yes" checked={formData.transfers === 'Yes'} onChange={handleChange} className="text-[#28CFD4] focus:ring-[#28CFD4] w-4 h-4"/> Yes</label>
                  <label className="flex items-center gap-2 cursor-pointer p-3 border border-stone-100 rounded-xl"><input type="radio" name="transfers" value="No" checked={formData.transfers === 'No'} onChange={handleChange} className="text-[#28CFD4] focus:ring-[#28CFD4] w-4 h-4"/> No</label>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white p-8 rounded-2xl shadow-sm border border-stone-100">
          <h3 className="text-xl font-serif font-semibold text-[#162635] mb-6 flex items-center gap-3">
            <div className="p-2.5 bg-[#F0F8F8] rounded-full"><ClipboardList size={20} className="text-[#28CFD4]"/></div> Rider Profile
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
            <div><label className="block text-sm font-bold text-stone-700 mb-1.5">Age</label><input required type="number" name="age" value={formData.age} onChange={handleChange} className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#28CFD4] outline-none" /></div>
            <div><label className="block text-sm font-bold text-stone-700 mb-1.5">Height (cm/ft)</label><input required type="text" name="height" value={formData.height} onChange={handleChange} className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#28CFD4] outline-none" /></div>
            <div><label className="block text-sm font-bold text-stone-700 mb-1.5">Weight (kg/st)</label><input required type="text" name="weight" value={formData.weight} onChange={handleChange} className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#28CFD4] outline-none" /></div>
          </div>
          <div className="mb-8">
            <label className="block text-sm font-bold text-stone-700 mb-4">Riding Ability</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[{ id: 'nonRider', label: 'Non-Riding Guest' }, { id: 'neverRidden', label: 'Never Ridden' }, { id: 'beginner', label: 'Beginner' }, { id: 'intermediate', label: 'Intermediate' }, { id: 'advanced', label: 'Advanced' }, { id: 'hackingWalkTrot', label: 'Hacking (Walk/Trot)' }, { id: 'hackingCanterGallop', label: 'Hacking (Canter/Gallop)' }].map(ability => (
                <label key={ability.id} className="flex items-center gap-3 p-4 border border-stone-200 rounded-xl cursor-pointer">
                  <input type="checkbox" name={`ability_${ability.id}`} checked={formData.abilities[ability.id]} onChange={handleChange} className="w-5 h-5 text-[#28CFD4] rounded" /> <span className="text-sm font-bold">{ability.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div><label className="block text-sm font-bold text-stone-700 mb-1.5">Describe riding experience</label><textarea required name="experience" value={formData.experience} onChange={handleChange} rows="3" className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#28CFD4] outline-none"></textarea></div>
        </section>

        <section className="bg-white p-8 rounded-2xl shadow-sm border border-stone-100">
          <h3 className="text-xl font-serif font-semibold text-[#162635] mb-6 flex items-center gap-3">
            <div className="p-2.5 bg-[#F0F8F8] rounded-full"><Utensils size={20} className="text-[#28CFD4]"/></div> Health & Dietary
          </h3>
          <div className="space-y-8">
            <div>
              <label className="block text-sm font-bold text-stone-700 mb-3">Dietary requirements?</label>
              <div className="flex gap-4 mb-3">
                  <label className="flex items-center gap-2 cursor-pointer p-3 border border-stone-100 rounded-xl"><input type="radio" name="hasDietary" value="Yes" checked={formData.hasDietary === 'Yes'} onChange={handleChange} className="text-[#28CFD4] w-4 h-4"/> Yes</label>
                  <label className="flex items-center gap-2 cursor-pointer p-3 border border-stone-100 rounded-xl"><input type="radio" name="hasDietary" value="No" checked={formData.hasDietary === 'No'} onChange={handleChange} className="text-[#28CFD4] w-4 h-4"/> No</label>
              </div>
              {formData.hasDietary === 'Yes' && <input type="text" name="dietaryDetails" value={formData.dietaryDetails} onChange={handleChange} className="w-full p-3 border border-stone-200 rounded-xl outline-none" />}
            </div>
            <div>
              <label className="block text-sm font-bold text-stone-700 mb-3">Medical conditions/injuries?</label>
              <div className="flex gap-4 mb-3">
                  <label className="flex items-center gap-2 cursor-pointer p-3 border border-stone-100 rounded-xl"><input type="radio" name="hasMedical" value="Yes" checked={formData.hasMedical === 'Yes'} onChange={handleChange} className="text-[#28CFD4] w-4 h-4"/> Yes</label>
                  <label className="flex items-center gap-2 cursor-pointer p-3 border border-stone-100 rounded-xl"><input type="radio" name="hasMedical" value="No" checked={formData.hasMedical === 'No'} onChange={handleChange} className="text-[#28CFD4] w-4 h-4"/> No</label>
              </div>
              {formData.hasMedical === 'Yes' && <input type="text" name="medicalDetails" value={formData.medicalDetails} onChange={handleChange} className="w-full p-3 border border-stone-200 rounded-xl outline-none" />}
            </div>
            <div>
              <label className="block text-sm font-bold text-stone-700 mb-3">Additional learning/communication needs?</label>
              <div className="flex gap-4 mb-3">
                  <label className="flex items-center gap-2 cursor-pointer p-3 border border-stone-100 rounded-xl"><input type="radio" name="hasNeeds" value="Yes" checked={formData.hasNeeds === 'Yes'} onChange={handleChange} className="text-[#28CFD4] w-4 h-4"/> Yes</label>
                  <label className="flex items-center gap-2 cursor-pointer p-3 border border-stone-100 rounded-xl"><input type="radio" name="hasNeeds" value="No" checked={formData.hasNeeds === 'No'} onChange={handleChange} className="text-[#28CFD4] w-4 h-4"/> No</label>
              </div>
              {formData.hasNeeds === 'Yes' && <input type="text" name="needsDetails" value={formData.needsDetails} onChange={handleChange} className="w-full p-3 border border-stone-200 rounded-xl outline-none" />}
            </div>
          </div>
        </section>

        <section className="bg-white p-8 rounded-2xl shadow-sm border border-stone-100">
          <h3 className="text-xl font-serif font-semibold text-[#162635] mb-6 flex items-center gap-3">
            <div className="p-2.5 bg-[#F0F8F8] rounded-full"><AlertCircle size={20} className="text-[#28CFD4]"/></div> Legal & Consent
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
            <div><label className="block text-sm font-bold text-stone-700 mb-1.5">Emergency Contact</label><input required type="text" name="emergencyContact" value={formData.emergencyContact} onChange={handleChange} className="w-full p-3 border border-stone-200 rounded-xl outline-none" /></div>
            <div><label className="block text-sm font-bold text-stone-700 mb-1.5">Emergency Phone</label><input required type="tel" name="emergencyPhone" value={formData.emergencyPhone} onChange={handleChange} className="w-full p-3 border border-stone-200 rounded-xl outline-none" /></div>
          </div>
          <div className="mb-8 pb-8 border-b border-stone-100">
            <label className="block text-sm font-bold text-stone-700 mb-4">Have insurance?</label>
            <div className="flex flex-col gap-3 mb-4">
              <label className="flex items-center gap-3 p-4 border border-stone-200 rounded-xl cursor-pointer"><input type="radio" name="hasInsurance" value="Yes" checked={formData.hasInsurance === 'Yes'} onChange={handleChange} className="text-[#28CFD4] w-5 h-5" /> <span className="font-bold">Yes</span></label>
              <label className="flex items-center gap-3 p-4 border border-stone-200 rounded-xl cursor-pointer"><input type="radio" name="hasInsurance" value="No" checked={formData.hasInsurance === 'No'} onChange={handleChange} className="text-[#28CFD4] w-5 h-5" /> <span className="font-bold">No - pay €10/day</span></label>
            </div>
            {formData.hasInsurance === 'Yes' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5 p-6 bg-[#F0F8F8] rounded-2xl border border-stone-100">
                <div><label className="block text-sm font-bold text-stone-700 mb-1.5">Company</label><input type="text" name="insuranceCompany" value={formData.insuranceCompany} onChange={handleChange} className="w-full p-3 border border-white rounded-xl outline-none bg-white" /></div>
                <div><label className="block text-sm font-bold text-stone-700 mb-1.5">Policy No.</label><input type="text" name="policyNumber" value={formData.policyNumber} onChange={handleChange} className="w-full p-3 border border-white rounded-xl outline-none bg-white" /></div>
              </div>
            )}
          </div>
          <div className="mb-8">
            <label className="block text-sm font-bold text-stone-700 mb-3">Photo Consent</label>
            <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer p-3 border border-stone-100 rounded-xl"><input type="radio" name="photoConsent" value="Yes" checked={formData.photoConsent === 'Yes'} onChange={handleChange} className="text-[#28CFD4] w-4 h-4"/> Yes</label>
                <label className="flex items-center gap-2 cursor-pointer p-3 border border-stone-100 rounded-xl"><input type="radio" name="photoConsent" value="No" checked={formData.photoConsent === 'No'} onChange={handleChange} className="text-[#28CFD4] w-4 h-4"/> No</label>
            </div>
          </div>
          <div className="bg-[#F0F8F8] p-6 rounded-2xl border border-stone-100 mb-8">
            <h4 className="font-serif font-bold text-[#162635] mb-2 text-lg">Terms & Conditions</h4>
            <label className="flex items-start gap-3 cursor-pointer">
              <input required type="checkbox" name="termsAccepted" checked={formData.termsAccepted} onChange={handleChange} className="mt-1.5 w-5 h-5 text-[#28CFD4] rounded" />
              <span className="text-base font-bold text-[#162635]">I have read and agree to terms.</span>
            </label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div><label className="block text-sm font-bold text-stone-700 mb-1.5">Signature Name</label><input required type="text" name="signatureName" value={formData.signatureName} onChange={handleChange} className="w-full p-3 border border-stone-200 rounded-xl outline-none" /></div>
            <div><label className="block text-sm font-bold text-stone-700 mb-1.5">Date Signed</label><input required type="date" name="dateSigned" value={formData.dateSigned} onChange={handleChange} className="w-full p-3 border border-stone-200 rounded-xl outline-none" /></div>
          </div>
        </section>
        <button type="submit" disabled={submitting} className="w-full py-4 bg-[#28CFD4] text-white font-sans font-bold tracking-wide text-xl rounded-full shadow-lg transition-all disabled:opacity-50">
          {submitting ? 'Submitting...' : 'Submit Registration'}
        </button>
      </form>
    </div>
  );
};

const PrintView = ({ type, displayGuests }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="bg-[#F0F8F8] text-[#162635] text-xs border-b border-stone-200">
            <th className="p-4 font-bold uppercase tracking-wider border-r border-stone-200">Name</th>
            <th className="p-4 font-bold uppercase tracking-wider border-r border-stone-200">
              {type === 'stables' ? 'Arrival & Departure' : 'Arrival'}
            </th>
            {type === 'kitchen' && (
              <>
                <th className="p-4 font-bold uppercase tracking-wider border-r border-stone-200">Dietary Requirements</th>
                <th className="p-4 font-bold uppercase tracking-wider">Departure</th>
              </>
            )}
            {type === 'stables' && (
              <>
                <th className="p-4 font-bold uppercase tracking-wider border-r border-stone-200">Profile (Age/Ht/Wt)</th>
                <th className="p-4 font-bold uppercase tracking-wider border-r border-stone-200 w-48">Abilities</th>
                <th className="p-4 font-bold uppercase tracking-wider border-r border-stone-200">Health & Needs</th>
                <th className="p-4 font-bold uppercase tracking-wider">Insurance</th>
              </>
            )}
          </tr>
        </thead>
        {displayGuests.length === 0 ? (
          <tbody>
            <tr><td colSpan={type === 'stables' ? "6" : "4"} className="p-12 text-center text-stone-500 font-medium">No guests matching current filters.</td></tr>
          </tbody>
        ) : (
          displayGuests.map((guest, idx) => {
            const abilities = getAbilitiesList(guest);
            const bgClass = idx % 2 === 0 ? 'bg-white' : 'bg-stone-50/50';
            
            return (
              <tbody key={guest.id} className="break-inside-avoid print:break-inside-avoid">
                <tr className={`${type === 'kitchen' ? 'border-b border-stone-100' : ''} ${bgClass}`}>
                  <td className="p-4 border-r border-stone-100 align-top">
                    <div className="font-bold text-[#162635]">{guest.name}</div>
                    {guest.bookingHolder && <div className="text-xs text-stone-500 italic mt-0.5">Booking: {guest.bookingHolder}</div>}
                  </td>
                  <td className="p-4 border-r border-stone-100 text-sm align-top whitespace-nowrap">
                    <div className="mb-2">
                      {type === 'stables' && <div className="text-[10px] uppercase font-bold text-stone-400 leading-none mb-1">Arrival</div>}
                      <div className="font-bold text-[#162635]">{formatUKDate(guest.arrivalDate)}</div>
                      <div className="text-stone-500 mt-0.5">{guest.arrivalTime || 'Time TBA'}</div>
                      {(guest.arrivalFlightNo || guest.arrivalFlight) && <div className="text-xs font-bold text-[#28CFD4] mt-1">Flight: {guest.arrivalFlightNo || guest.arrivalFlight}</div>}
                    </div>
                    {type === 'stables' && (
                      <div className="mt-3">
                        <div className="text-[10px] uppercase font-bold text-stone-400 leading-none mb-1">Departure</div>
                        <div className="font-bold text-[#162635]">{formatUKDate(guest.departureDate)}</div>
                        <div className="text-stone-500 mt-0.5">{guest.departureTime || 'Time TBA'}</div>
                        {(guest.departureFlightNo || guest.departureFlight) && <div className="text-xs font-bold text-[#28CFD4] mt-1">Flight: {guest.departureFlightNo || guest.departureFlight}</div>}
                      </div>
                    )}
                  </td>
                  
                  {type === 'kitchen' && (
                    <>
                      <td className="p-4 border-r border-stone-100 text-sm align-top">
                        {guest.hasDietary === 'No' ? <span className="text-stone-400 italic">None</span> : <strong className="text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg inline-block">{guest.dietaryDetails || 'Yes (Check with guest)'}</strong>}
                      </td>
                      <td className="p-4 text-sm align-top whitespace-nowrap">
                        <div className="font-bold text-[#162635]">{formatUKDate(guest.departureDate)}</div>
                        <div className="text-stone-500 mt-0.5">{guest.departureTime || 'Time TBA'}</div>
                        {(guest.departureFlightNo || guest.departureFlight) && <div className="text-xs font-bold text-[#28CFD4] mt-1">Flight: {guest.departureFlightNo || guest.departureFlight}</div>}
                      </td>
                    </>
                  )}

                  {type === 'stables' && (
                    <>
                      <td className="p-4 border-r border-stone-100 text-sm align-top whitespace-nowrap pb-2">
                        <div className="mb-1"><span className="text-stone-400 text-xs uppercase inline-block w-6">Age:</span> <strong className="text-[#162635]">{guest.age}</strong></div>
                        <div className="mb-1"><span className="text-stone-400 text-xs uppercase inline-block w-6">Ht:</span> <strong className="text-[#162635]">{guest.height}</strong></div>
                        <div><span className="text-stone-400 text-xs uppercase inline-block w-6">Wt:</span> <strong className="text-[#162635]">{guest.weight}</strong></div>
                      </td>
                      <td className="p-4 border-r border-stone-100 text-sm align-top pb-2">
                          <ul className="list-disc pl-4 space-y-1">
                            {abilities.length > 0 ? abilities.map((ability, i) => (
                              <li key={i} className="font-bold text-[#28CFD4] leading-tight"><span className="text-[#162635]">{ability}</span></li>
                            )) : <li className="text-stone-400 italic list-none -ml-4">None stated</li>}
                          </ul>
                      </td>
                      <td className="p-4 border-r border-stone-100 text-sm align-top pb-2">
                        {guest.hasMedical === 'Yes' && <div className="mb-2"><span className="font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded text-xs uppercase">Med</span> <br/>{guest.medicalDetails || 'Yes'}</div>}
                        {guest.hasNeeds === 'Yes' && <div><span className="font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded text-xs uppercase">Needs</span> <br/>{guest.needsDetails || 'Yes'}</div>}
                        {guest.hasMedical !== 'Yes' && guest.hasNeeds !== 'Yes' && <span className="text-stone-400 italic">None</span>}
                      </td>
                      <td className="p-4 text-sm align-top pb-2">
                        {guest.hasInsurance === 'No' ? (
                          <strong className="text-rose-600">€10/day Cover</strong>
                        ) : (
                          <div>
                            <span className="font-bold text-[#28CFD4]">Covered</span>
                            {(guest.insuranceCompany || guest.policyNumber) && <div className="text-xs mt-1 text-stone-500 font-medium">{guest.insuranceCompany} <br/> {guest.policyNumber}</div>}
                          </div>
                        )}
                      </td>
                    </>
                  )}
                </tr>
                {type === 'stables' && (
                  <tr className={`border-b-2 border-stone-200 ${bgClass}`}>
                    <td colSpan="6" className="px-4 pb-4 pt-1 align-top">
                      <div className="flex gap-3">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-stone-400 mt-0.5 whitespace-nowrap">Experience:</span>
                        <div className="text-sm italic text-stone-700 whitespace-pre-wrap break-words leading-relaxed flex-1">
                          {guest.experience || 'Not provided'}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            );
          })
        )}
      </table>
    </div>
  </div>
);

const AssessmentPrintSheet = ({ displayGuests }) => (
  <div className="bg-transparent print:bg-white text-stone-900 w-full max-w-[210mm] mx-auto">
    <style type="text/css" media="print">
      {`
        @page { size: A4 portrait; margin: 10mm; }
        .print-page { page-break-after: always; break-after: page; }
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      `}
    </style>
    {displayGuests.length === 0 ? (
      <div className="bg-white p-12 text-center text-stone-500 rounded-2xl shadow-sm border border-stone-100">No guests matching current filters.</div>
    ) : (
      <div className="space-y-12 print:space-y-0">
        {displayGuests.map((guest) => (
          <div key={guest.id} className="print-page bg-white p-8 rounded-2xl shadow-md border border-stone-200 print:shadow-none print:border-none print:rounded-none h-[277mm] box-border overflow-hidden flex flex-col font-sans">
            <div className="text-center mb-6">
               <h1 className="text-4xl font-serif font-bold tracking-widest text-[#162635] uppercase">Hacienda Horses</h1>
               <h2 className="text-2xl font-bold uppercase tracking-widest mt-1 text-stone-500">Client Assessment</h2>
            </div>
            <div className="grid grid-cols-2 gap-x-12 gap-y-4 mb-6 text-base">
              <div className="border-b-2 border-stone-300 pb-1 flex justify-between"><span className="font-bold text-stone-700">Assessor Name:</span><span className="flex-1 ml-2"></span></div>
              <div className="border-b-2 border-stone-300 pb-1 flex justify-between"><span className="font-bold text-stone-700">Client Name:</span><span className="flex-1 ml-2 font-bold text-black text-right">{guest.name}</span></div>
              <div className="border-b-2 border-stone-300 pb-1 flex justify-between"><span className="font-bold text-stone-700">Horse:</span><span className="flex-1 ml-2"></span></div>
              <div className="border-b-2 border-stone-300 pb-1 flex justify-between"><span className="font-bold text-stone-700">Date:</span><span className="flex-1 ml-2 font-bold text-black text-right">{formatUKDate(guest.arrivalDate)}</span></div>
              <div className="border-b-2 border-stone-300 pb-1 flex justify-between"><span className="font-bold text-stone-700">Time:</span><span className="flex-1 ml-2"></span></div>
            </div>
            <h3 className="font-bold text-xl border-b-2 border-stone-800 mb-4 pb-1 text-[#162635]">Pre Assessment.</h3>
            <div className="space-y-3 mb-6 text-base">
              <div className="flex gap-6 items-center">
                <span className="w-80">Does the client have insurance?</span>
                <label className="flex items-center gap-2"><div className={`w-5 h-5 border-2 border-stone-800 flex items-center justify-center ${guest.hasInsurance === 'Yes' ? 'bg-black text-white' : ''}`}>{guest.hasInsurance === 'Yes' && '✓'}</div> Yes</label>
                <label className="flex items-center gap-2"><div className={`w-5 h-5 border-2 border-stone-800 flex items-center justify-center ${guest.hasInsurance === 'No' ? 'bg-black text-white' : ''}`}>{guest.hasInsurance === 'No' && '✓'}</div> No</label>
              </div>
              <div className="flex gap-6 items-center">
                <span className="w-80">Filled in In-House Insurance form?</span>
                <label className="flex items-center gap-2"><div className="w-5 h-5 border-2 border-stone-800"></div> Yes</label>
                <label className="flex items-center gap-2"><div className="w-5 h-5 border-2 border-stone-800"></div> No</label>
                <label className="flex items-center gap-2"><div className={`w-5 h-5 border-2 border-stone-800 flex items-center justify-center ${guest.hasInsurance === 'Yes' ? 'bg-black text-white' : ''}`}>{guest.hasInsurance === 'Yes' && '✓'}</div> NA</label>
              </div>
            </div>
            <h3 className="font-bold text-xl border-b-2 border-stone-800 mb-4 pb-1 text-[#162635]">Verbally confirm riding ability with client, is client comfortable?</h3>
            <div className="grid grid-cols-2 gap-12 mb-6 text-base">
              <div>
                <h4 className="font-bold mb-3 italic text-stone-600">In The School</h4>
                <div className="space-y-2">
                   <label className="flex items-center gap-3"><div className="w-5 h-5 border-2 border-stone-800"></div> Walk</label>
                   <label className="flex items-center gap-3"><div className="w-5 h-5 border-2 border-stone-800"></div> Trot</label>
                   <label className="flex items-center gap-3"><div className="w-5 h-5 border-2 border-stone-800"></div> Canter</label>
                   <label className="flex items-center gap-3"><div className="w-5 h-5 border-2 border-stone-800"></div> Jumping</label>
                </div>
              </div>
              <div>
                <h4 className="font-bold mb-3 italic text-stone-600">Out on a Hack</h4>
                <div className="space-y-2">
                   <label className="flex items-center gap-3"><div className="w-5 h-5 border-2 border-stone-800"></div> Walk</label>
                   <label className="flex items-center gap-3"><div className="w-5 h-5 border-2 border-stone-800"></div> Trot</label>
                   <label className="flex items-center gap-3"><div className="w-5 h-5 border-2 border-stone-800"></div> Canter</label>
                </div>
              </div>
              <div className="col-span-2 flex items-end gap-4 mt-2">
                 <span className="font-bold">Determine client confidence level out of 10:</span><div className="border-b-2 border-stone-400 w-32"></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-12 mb-6 text-base">
              <div>
                <h3 className="font-bold text-xl border-b-2 border-stone-800 mb-3 pb-1 text-[#162635]">Assessment</h3>
                <div className="space-y-2">
                   <div className="flex justify-between items-center"><span className="w-48">Can they correctly mount?</span><div className="w-6 h-6 border-2 border-stone-800"></div></div>
                   <div className="flex justify-between items-center"><span className="w-48">Do they have reign contact?</span><div className="w-6 h-6 border-2 border-stone-800"></div></div>
                   <div className="flex justify-between items-center"><span className="w-48">Can they trot?</span><div className="w-6 h-6 border-2 border-stone-800"></div></div>
                   <div className="flex justify-between items-center"><span className="w-48">Can they stop?</span><div className="w-6 h-6 border-2 border-stone-800"></div></div>
                   <div className="flex justify-between items-center"><span className="w-48">Are they balanced?</span><div className="w-6 h-6 border-2 border-stone-800"></div></div>
                   <div className="flex justify-between items-center"><span className="w-48">Can they turn left?</span><div className="w-6 h-6 border-2 border-stone-800"></div></div>
                   <div className="flex justify-between items-center"><span className="w-48">Can they turn right?</span><div className="w-6 h-6 border-2 border-stone-800"></div></div>
                   <div className="flex justify-between items-center"><span className="w-48">Can they leg yield?</span><div className="w-6 h-6 border-2 border-stone-800"></div></div>
                </div>
              </div>
              <div>
                <h3 className="font-bold text-xl border-b-2 border-stone-800 mb-3 pb-1 text-[#162635] leading-tight">Based on my assessment I believe<br/>the client riding ability to be:</h3>
                <div className="space-y-3 mt-4">
                   <label className="flex items-start gap-4"><div className="w-6 h-6 border-2 border-stone-800 shrink-0 mt-0.5"></div><div className="font-bold">Beginner</div></label>
                   <label className="flex items-start gap-4"><div className="w-6 h-6 border-2 border-stone-800 shrink-0 mt-0.5"></div><div className="font-bold">Novice</div></label>
                   <label className="flex items-start gap-4"><div className="w-6 h-6 border-2 border-stone-800 shrink-0 mt-0.5"></div><div className="font-bold">Intermediate</div></label>
                   <label className="flex items-start gap-4"><div className="w-6 h-6 border-2 border-stone-800 shrink-0 mt-0.5"></div><div className="font-bold">Advanced</div></label>
                </div>
              </div>
            </div>
            
            <div className="mb-4">
              <h3 className="font-bold text-xl border-b-2 border-stone-800 mb-2 pb-1 text-[#162635]">Guest's Riding Goals / Aspirations for the Holiday</h3>
              <div className="border-2 border-stone-300 rounded-lg h-24 mt-2"></div>
            </div>

            <div className="mt-auto pt-2">
              <div className="text-center font-bold text-rose-600 uppercase tracking-widest border-y-2 border-rose-200 py-2 mb-4 text-sm">Please advise client that the first hack will be walk, trot only</div>
              <div className="flex items-end gap-3 w-1/2 mb-4"><span className="font-bold text-lg">Signed:</span><div className="border-b-2 border-stone-400 flex-1"></div></div>
              <div className="text-center text-xs font-bold text-stone-400">www.haciendahorses.com &nbsp;|&nbsp; Cortijo Uribe - Mollina - Malaga - 29532</div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const InsurancePrintSheet = ({ displayGuests }) => {
  const insuranceGuests = displayGuests.filter(g => g.hasInsurance === 'No');
  const calculateDays = (start, end) => {
    if (!start || !end) return 0;
    const diffTime = Math.abs(new Date(end) - new Date(start));
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; 
  };
  return (
    <div className="bg-transparent print:bg-white text-stone-900 w-full max-w-[210mm] mx-auto">
      <style type="text/css" media="print">
        {`
          @page { size: A4 portrait; margin: 10mm; }
          .print-page { page-break-after: always; break-after: page; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        `}
      </style>
      {insuranceGuests.length === 0 ? (
        <div className="bg-white p-12 text-center text-stone-500 rounded-2xl shadow-sm border border-stone-100">No guests requiring insurance in current view.</div>
      ) : (
        <div className="space-y-12 print:space-y-0">
          {insuranceGuests.map((guest) => {
            const days = calculateDays(guest.arrivalDate, guest.departureDate);
            const cost = days * 10;
            const firstName = guest.name ? guest.name.split(' ')[0] : '';
            const lastName = guest.name && guest.name.includes(' ') ? guest.name.substring(guest.name.indexOf(' ') + 1) : '';
            return (
              <div key={guest.id} className="print-page bg-white p-10 md:p-12 rounded-2xl shadow-md border border-stone-200 print:shadow-none print:border-none print:rounded-none h-[277mm] box-border overflow-hidden flex flex-col font-sans relative">
                <div className="text-center mb-10">
                   <h1 className="text-4xl font-serif font-bold tracking-widest text-[#162635] uppercase mb-2">Hacienda Horses</h1>
                   <h2 className="text-3xl font-serif font-bold uppercase tracking-widest text-stone-800">Riding Insurance</h2>
                </div>
                <div className="space-y-6 mb-10 text-lg">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-2"><span className="text-sm font-bold text-stone-500 block mb-1">Title</span><div className="border-b-2 border-stone-300 pb-1 h-8"></div></div>
                    <div className="col-span-5"><span className="text-sm font-bold text-stone-500 block mb-1">First Name</span><div className="border-b-2 border-stone-300 pb-1 h-8 font-bold">{firstName}</div></div>
                    <div className="col-span-5"><span className="text-sm font-bold text-stone-500 block mb-1">Last Name</span><div className="border-b-2 border-stone-300 pb-1 h-8 font-bold">{lastName}</div></div>
                  </div>
                  <div className="grid grid-cols-2 gap-8 items-end">
                    <div><span className="text-sm font-bold text-stone-500 block mb-1">Email:</span><div className="border-b-2 border-stone-300 pb-1 h-8 font-bold text-base">{guest.email}</div></div>
                    <div><span className="text-sm font-bold text-stone-500 block mb-1">Phone:</span><div className="border-b-2 border-stone-300 pb-1 h-8 font-bold text-base">{guest.phone}</div></div>
                  </div>
                </div>
                <p className="text-center text-xl font-medium text-[#162635] mb-8 px-4">I confirm that I would like to be covered by the<br/>riding accident policy at Hacienda Horses.</p>
                <div className="bg-[#5CE1E6] rounded-3xl p-8 max-w-xl mx-auto w-full border-4 border-[#5CE1E6] print:border-none print:bg-[#5CE1E6] text-[#162635]">
                  <div className="space-y-6 text-xl">
                    <div className="flex justify-between items-center border-b border-black/20 pb-2"><span className="font-medium">Insurance Start Date</span><span className="font-bold tracking-widest">{formatUKDate(guest.arrivalDate)}</span></div>
                    <div className="flex justify-between items-center border-b border-black/20 pb-2"><span className="font-medium">Insurance End Date</span><span className="font-bold tracking-widest">{formatUKDate(guest.departureDate)}</span></div>
                    <div className="flex justify-between items-center border-b border-black/20 pb-2"><span className="font-medium">Total Days Covered</span><span className="font-bold">{days}</span></div>
                    <div className="flex justify-between items-center border-b border-black/20 pb-2"><span className="font-medium">Total Cost</span><span className="font-bold text-2xl">€{cost}</span></div>
                    <div className="pt-8 flex justify-between items-end"><span className="font-medium">Signature</span><div className="w-64 border-b-2 border-[#162635]"></div></div>
                  </div>
                </div>
                <div className="mt-auto pt-4 text-center text-sm font-bold text-stone-400">www.haciendahorses.com &nbsp;|&nbsp; Cortijo Uribe - Mollina - Malaga - 29532</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const RotaView = ({ currentWeekStart, handlePrevWeek, handleNextWeek, loadPastRotas, isSavingRota, handleSaveRota, weekDates, staff, syncGuestsFromBookings, guestCounts, updateGuestCount, shifts, toggleShift, isDateOfficialHoliday, getShiftIcon }) => (
  <div className="rota-view animate-fade-in w-full pb-24">
    <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-stone-200 no-print">
      <div className="flex items-center gap-4">
        <button onClick={handlePrevWeek} className="p-2 hover:bg-stone-100 rounded-full transition-colors"><ChevronLeft className="w-6 h-6 text-stone-600" /></button>
        <div className="text-center">
          <h2 className="text-lg font-bold text-stone-800">Week of {currentWeekStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</h2>
        </div>
        <button onClick={handleNextWeek} className="p-2 hover:bg-stone-100 rounded-full transition-colors"><ChevronRight className="w-6 h-6 text-stone-600" /></button>
      </div>
      <div className="flex flex-wrap justify-end gap-3">
        <button onClick={loadPastRotas} className="flex items-center gap-2 px-3 py-2 text-stone-600 bg-white border border-stone-300 rounded-lg hover:bg-stone-50 transition-colors text-sm font-medium">
          <History className="w-4 h-4" /> History
        </button>
        <button onClick={() => window.print()} className="flex items-center gap-2 px-3 py-2 text-stone-600 bg-white border border-stone-300 rounded-lg hover:bg-stone-50 transition-colors text-sm font-medium">
          <Printer className="w-4 h-4" /> Print
        </button>
        <button onClick={handleSaveRota} disabled={isSavingRota} className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white font-medium rounded-lg hover:bg-cyan-700 shadow-sm transition-colors text-sm disabled:opacity-50">
          {isSavingRota ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {isSavingRota ? 'Saving...' : 'Save Rota'}
        </button>
      </div>
    </div>

    <div className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden print-container mt-6">
      <div className="hidden print:block p-4 border-b border-stone-200">
        <h1 className="text-2xl font-bold text-cyan-800">Hacienda Horses - Staff Rota</h1>
        <p className="text-stone-600">Week Commencing: {currentWeekStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px] border-collapse">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-200">
              <th className="p-4 text-left w-48 font-bold text-stone-600 uppercase text-xs tracking-wider sticky left-0 bg-stone-50 z-10 print:static">Role / Name</th>
              {weekDates.map(date => (
                <th key={date.toString()} className="p-3 text-center min-w-[100px] border-l border-stone-100">
                  <div className="text-xs font-semibold text-cyan-600 uppercase">{date.toLocaleDateString('en-GB', { weekday: 'short' })}</div>
                  <div className="text-lg font-bold text-stone-800">{date.getDate()}</div>
                </th>
              ))}
            </tr>
            <tr className="bg-cyan-50/50">
              <td className="p-3 font-bold text-cyan-800 text-sm border-r border-cyan-100 sticky left-0 bg-cyan-50/90 backdrop-blur z-10 print:static">
                <div className="flex items-center justify-between">
                  <span>GUESTS (AM)</span>
                  <button onClick={syncGuestsFromBookings} title="Sync from Occupancy Calendar" className="text-cyan-600 hover:text-cyan-900 p-1 rounded hover:bg-cyan-200 transition-colors no-print"><RefreshCw className="w-4 h-4" /></button>
                </div>
              </td>
              {weekDates.map(date => {
                const dKey = formatDateKey(date);
                const count = guestCounts[dKey]?.am || 0;
                return (
                  <td key={`guest-am-${dKey}`} className="p-2 border-l border-cyan-100 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => updateGuestCount(dKey, 'am', -1)} className="text-cyan-400 hover:text-cyan-600 text-xs font-bold w-4 h-4 flex items-center justify-center rounded no-print">-</button>
                      <span className="font-bold text-cyan-700 w-4">{count}</span>
                      <button onClick={() => updateGuestCount(dKey, 'am', 1)} className="text-cyan-400 hover:text-cyan-600 text-xs font-bold w-4 h-4 flex items-center justify-center rounded no-print">+</button>
                    </div>
                  </td>
                );
              })}
            </tr>
            <tr className="bg-cyan-50/50 border-b-2 border-cyan-100">
              <td className="p-3 font-bold text-cyan-800 text-sm border-r border-cyan-100 sticky left-0 bg-cyan-50/90 backdrop-blur z-10 print:static">
                <div className="flex items-center justify-between">
                  <span>GUESTS (PM)</span>
                  <button onClick={syncGuestsFromBookings} title="Sync from Occupancy Calendar" className="text-cyan-600 hover:text-cyan-900 p-1 rounded hover:bg-cyan-200 transition-colors no-print"><RefreshCw className="w-4 h-4" /></button>
                </div>
              </td>
              {weekDates.map(date => {
                const dKey = formatDateKey(date);
                const count = guestCounts[dKey]?.pm || 0;
                return (
                  <td key={`guest-pm-${dKey}`} className="p-2 border-l border-cyan-100 text-center">
                      <div className="flex items-center justify-center gap-2">
                      <button onClick={() => updateGuestCount(dKey, 'pm', -1)} className="text-cyan-400 hover:text-cyan-600 text-xs font-bold w-4 h-4 flex items-center justify-center rounded no-print">-</button>
                      <span className="font-bold text-cyan-700 w-4">{count}</span>
                      <button onClick={() => updateGuestCount(dKey, 'pm', 1)} className="text-cyan-400 hover:text-cyan-600 text-xs font-bold w-4 h-4 flex items-center justify-center rounded no-print">+</button>
                    </div>
                  </td>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {ROLES.map(role => {
              const roleStaff = staff.filter(s => {
                  if (s.role !== role) return false;
                  if (!s.endDate) return true;
                  const endDate = new Date(s.endDate);
                  endDate.setHours(23, 59, 59, 999);
                  return endDate >= currentWeekStart;
              });
              if (roleStaff.length === 0) return null;
              const visibleStaffCount = roleStaff.filter(s => s.showOnPrint !== false).length;
              return (
                <React.Fragment key={role}>
                  <tr className={`bg-stone-100 ${visibleStaffCount === 0 ? 'print:hidden' : ''}`}><td colSpan={8} className="p-2 pl-4 text-xs font-bold text-stone-500 uppercase tracking-widest border-y border-stone-200">{role}</td></tr>
                  {roleStaff.map(employee => (
                    <tr key={employee.id} className={`hover:bg-stone-50 transition-colors border-b border-stone-100 group ${employee.showOnPrint === false ? 'print:hidden' : ''}`}>
                      <td className="p-3 text-sm font-medium text-stone-700 sticky left-0 bg-white group-hover:bg-stone-50 border-r border-stone-100 z-10 print:static">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-stone-200 flex items-center justify-center text-stone-500 text-[10px] font-bold print:hidden">{employee.name.substring(0, 1)}</div>
                          {employee.name}
                        </div>
                      </td>
                      {weekDates.map(date => {
                        const dKey = formatDateKey(date);
                        const shiftKey = `${dKey}_${employee.id}`;
                        const isHol = isDateOfficialHoliday(dKey, employee.id);
                        const shift = shifts[shiftKey];
                        return (
                          <td 
                            key={shiftKey} 
                            onClick={() => toggleShift(dKey, employee.id)}
                            className={`p-1 text-center border-l border-stone-100 cursor-pointer select-none transition-all duration-200
                              ${!isHol && shift === 'OFF' ? 'bg-red-50/50' : ''}
                              ${!isHol && shift === 'FULL' ? 'bg-cyan-50/30' : ''}
                              ${!isHol && shift === 'SICK' ? 'bg-yellow-50/50' : ''}
                              hover:bg-stone-100 active:scale-95
                            `}
                          >
                            <div className="flex items-center justify-center w-full min-h-[24px]">
                              {getShiftIcon(shift, isHol)}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const OccupancyView = ({ 
  occupancyDate, setOccupancyDate, icalUrl, fetchIcalData, isLoadingIcal, isSavingIcal, handleSetUrl, handleFileUpload, 
  events, rooms, openEditEventModal, guests, isKitchenMode, openAddBookingModal 
}) => {
  const [translations, setTranslations] = useState({});

  const daysInOccupancyMonth = useMemo(() => {
      const year = occupancyDate.getFullYear();
      const month = occupancyDate.getMonth();
      const date = new Date(year, month, 1);
      const days = [];
      while (date.getMonth() === month) {
          days.push(new Date(date));
          date.setDate(date.getDate() + 1);
      }
      return days;
  }, [occupancyDate]);

  const dietaryAppendix = useMemo(() => {
      if (!isKitchenMode) return [];
      let counter = 1;
      const notes = [];
      const seenFormIds = new Set();
      
      const monthStart = daysInOccupancyMonth[0];
      const monthEnd = daysInOccupancyMonth[daysInOccupancyMonth.length - 1];

      events.forEach(ev => {
          if (ev.endDate <= monthStart || ev.startDate > monthEnd) return;
          const matchingForms = getMatchingForms(ev, guests);
          
          matchingForms.forEach(form => {
              if (form.hasDietary === 'Yes' && !seenFormIds.has(form.id)) {
                  seenFormIds.add(form.id);
                  notes.push({
                      refId: counter++,
                      room: ev.room,
                      bookingName: ev.guest,
                      guestName: form.name.split(' ')[0], 
                      details: form.dietaryDetails,
                      summary: generateDietarySummary(form.dietaryDetails)
                  });
              }
          });
      });
      return notes;
  }, [events, guests, daysInOccupancyMonth, isKitchenMode]);

  useEffect(() => {
    if (!isKitchenMode || dietaryAppendix.length === 0) return;
    
    let isMounted = true;

    const fetchTranslations = async () => {
      const newTranslations = { ...translations };
      let hasNew = false;

      for (const note of dietaryAppendix) {
        if (!newTranslations[note.refId]) {
          try {
            const res = await fetch(`https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=es&dt=t&q=${encodeURIComponent(note.details)}`);
            const data = await res.json();
            if (data && data[0]) {
               const translatedText = data[0].map(item => item[0]).join('');
               newTranslations[note.refId] = translatedText;
               hasNew = true;
            }
          } catch (err) {
            console.error("Translation error:", err);
            newTranslations[note.refId] = "Traducción no disponible.";
            hasNew = true;
          }
        }
      }
      if (hasNew && isMounted) {
        setTranslations(newTranslations);
      }
    };

    fetchTranslations();
    
    return () => { isMounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dietaryAppendix, isKitchenMode]);

  const getOccupancyTotals = () => {
      const amTotals = [];
      const pmTotals = [];
      const amNonRiders = [];
      const pmNonRiders = [];
      daysInOccupancyMonth.forEach(day => {
          let am = 0; let pm = 0;
          let amNr = 0; let pmNr = 0;
          const targetDayStart = new Date(day).setHours(0,0,0,0);
          const yesterdayStart = new Date(addDays(day, -1)).setHours(0,0,0,0);
          
          events.forEach(ev => {
              const evStart = new Date(ev.startDate).setHours(0,0,0,0);
              const evEnd = new Date(ev.endDate).setHours(0,0,0,0);
              
              const isPm = targetDayStart >= evStart && targetDayStart < evEnd;
              const isAm = yesterdayStart >= evStart && yesterdayStart < evEnd;
              
              if (isPm || isAm) {
                  const matchingForms = getMatchingForms(ev, guests);
                  const nrCount = matchingForms.filter(checkIsNonRider).length;
                  
                  if (isPm) { pm += ev.occupancy; pmNr += nrCount; }
                  if (isAm) { am += ev.occupancy; amNr += nrCount; }
              }
          });
          amTotals.push(am); pmTotals.push(pm);
          amNonRiders.push(amNr); pmNonRiders.push(pmNr);
      });
      return { amTotals, pmTotals, amNonRiders, pmNonRiders };
  };
  const { amTotals: occAmTotals, pmTotals: occPmTotals, amNonRiders: occAmNonRiders, pmNonRiders: occPmNonRiders } = getOccupancyTotals();

  const renderOccupancyRowCells = (room) => {
      const cells = [];
      let dayIndex = 0;
      while (dayIndex < daysInOccupancyMonth.length) {
          const day = daysInOccupancyMonth[dayIndex];
          const booking = events.find(e => e.room === room && day >= e.startDate && day < e.endDate);
          if (booking) {
              let span = 1;
              while (dayIndex + span < daysInOccupancyMonth.length) {
                  const nextDay = daysInOccupancyMonth[dayIndex + span];
                  if (nextDay < booking.endDate) span++;
                  else break;
              }
              const isStart = day.getTime() === booking.startDate.getTime();
              const endOfSpanDate = new Date(day);
              endOfSpanDate.setDate(day.getDate() + span);
              const isEnd = endOfSpanDate.getTime() === booking.endDate.getTime();
              const roundedClasses = `${isStart ? 'rounded-l-md border-l-4' : 'rounded-l-none border-l-0'} ${isEnd ? 'rounded-r-md' : 'rounded-r-none border-r-0'}`;
              
              const matchingForms = getMatchingForms(booking, guests);
              const dietaryForms = matchingForms.filter(g => g.hasDietary === 'Yes');

              cells.push(
                  <td key={day.toISOString()} colSpan={span} className="p-1 border-r border-b border-stone-200 relative hover:bg-stone-50 transition cursor-pointer" onClick={() => openEditEventModal(booking)}>
                      <div className={`w-full shadow-sm flex flex-col px-2 overflow-hidden gap-1 ${booking.isManualBooking ? 'bg-purple-200 text-purple-900 border-purple-500' : 'bg-cyan-200 text-cyan-900 border-cyan-500'} ${roundedClasses} ${isKitchenMode && dietaryForms.length > 0 ? 'min-h-[50px] py-1' : 'h-10 justify-center'}`}>
                          <div className="flex items-center justify-between w-full">
                              <span className="text-[10px] md:text-xs font-bold truncate flex-1 text-left">
                                  {booking.guest} {booking.isManualOverride && <span className="text-red-500 font-bold ml-1" title="Manually Extended">*</span>} {booking.isManualBooking && <span className="text-purple-600 font-bold ml-1" title="Manual Booking">(M)</span>}
                                  {booking.isManualRoomOverride && <span className="text-[8px] font-bold text-orange-600 bg-orange-100 px-1 py-0.5 rounded ml-1 uppercase" title={`Moved from ${booking.originalRoom}`}>Moved</span>}
                              </span>
                              <span className="text-[10px] md:text-xs font-bold shrink-0">({booking.occupancy})</span>
                          </div>
                          {isKitchenMode && dietaryForms.map((df, i) => {
                              const note = dietaryAppendix.find(n => n.guestName === df.name.split(' ')[0] && n.details === df.dietaryDetails);
                              if (!note) return null;
                              return (
                                  <div key={i} className="bg-orange-100 border border-orange-300 text-orange-900 text-[9px] leading-tight font-bold px-1 py-0.5 rounded w-full whitespace-nowrap overflow-hidden text-ellipsis print:text-[8px] print:py-[1px]">
                                      <span className="text-red-600">[{note.refId}]</span> {note.guestName}: {note.summary}
                                  </div>
                              );
                          })}
                      </div>
                  </td>
              );
              dayIndex += span;
          } else {
              cells.push(<td key={day.toISOString()} className="border-r border-b border-stone-200 h-12 bg-white"></td>);
              dayIndex++;
          }
      }
      return cells;
  };

  return (
    <div className="occupancy-view animate-fade-in flex flex-col w-full pb-24">
      <div className="bg-white border border-stone-200 rounded-xl p-4 shadow-sm mb-6 no-print">
        <div className="max-w-full mx-auto flex flex-col xl:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
              <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  <CalendarDays className="w-6 h-6 text-cyan-600" /> {isKitchenMode ? "Kitchen Calendar" : "Occupancy Calendar"}
              </h2>
              <div className="flex items-center bg-slate-100 rounded-lg p-1">
                  <button onClick={() => setOccupancyDate(new Date(occupancyDate.setMonth(occupancyDate.getMonth() - 1)))} className="p-1 hover:bg-white rounded shadow-sm transition"><ChevronLeft className="w-5 h-5" /></button>
                  <span className="px-4 font-semibold w-40 text-center text-slate-700">{occupancyDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</span>
                  <button onClick={() => setOccupancyDate(new Date(occupancyDate.setMonth(occupancyDate.getMonth() + 1)))} className="p-1 hover:bg-white rounded shadow-sm transition"><ChevronRight className="w-5 h-5" /></button>
              </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 justify-center">
              <button onClick={openAddBookingModal} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 shadow-sm transition"><CalendarPlus size={16} /> Add Booking</button>
              
              {icalUrl ? (
                  <div className="flex items-center gap-2 bg-cyan-50 px-3 py-1.5 rounded-md border border-cyan-100">
                      <button onClick={() => fetchIcalData(icalUrl)} disabled={isLoadingIcal || isSavingIcal} className="flex items-center gap-2 text-sm font-medium text-cyan-700 hover:text-cyan-900">
                          {isLoadingIcal || isSavingIcal ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                          {isLoadingIcal ? "Fetching..." : isSavingIcal ? "Syncing..." : "Refresh Data"}
                      </button>
                      <span className="text-cyan-300">|</span>
                      <button onClick={handleSetUrl} className="text-xs text-cyan-600 hover:text-cyan-800 underline">Change Link</button>
                  </div>
              ) : (
                  <button onClick={handleSetUrl} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50"><LinkIcon className="w-4 h-4" /> Set Auto-Refresh Link</button>
              )}
              <label className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-md hover:bg-slate-50 cursor-pointer">
                  <Upload className="w-4 h-4" /> Import File
                  <input type="file" accept=".ics" onChange={handleFileUpload} className="hidden" />
              </label>
              <button onClick={() => window.print()} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700 shadow-sm"><Printer className="w-4 h-4" /> Print</button>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-slate-200 print-container">
          <div className="hidden print:flex justify-between items-center p-4 border-b border-slate-200">
              <h2 className="text-2xl font-bold text-cyan-800">{isKitchenMode ? "Kitchen Calendar" : "Occupancy Report"}</h2>
              <h3 className="text-xl text-slate-600">{occupancyDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</h3>
          </div>
          <div className="overflow-x-auto">
              <table className="w-full border-collapse table-fixed">
                  <thead>
                      <tr>
                          <th className="sticky left-0 z-10 bg-slate-50 border-b border-r border-slate-200 p-2 w-48 text-left text-xs font-bold uppercase text-slate-500 tracking-wider">Room Name</th>
                          {daysInOccupancyMonth.map(day => (
                              <th key={day.toISOString()} className="border-b border-r border-slate-100 p-1 text-center bg-slate-50">
                                  <div className="text-[10px] font-semibold text-slate-400 uppercase">{day.toLocaleDateString('en-GB', { weekday: 'short' })}</div>
                                  <div className="text-sm font-bold text-slate-700">{day.getDate()}</div>
                              </th>
                          ))}
                      </tr>
                  </thead>
                  <tbody>
                      {rooms.map((room, rIdx) => (
                          <tr key={room} className={rIdx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                              <td className="sticky left-0 z-10 bg-inherit border-r border-slate-200 p-3 text-sm font-semibold text-slate-800 truncate border-b border-slate-100">{room}</td>
                              {renderOccupancyRowCells(room)}
                          </tr>
                      ))}
                      <tr className="bg-slate-700 text-white font-bold sticky bottom-0 z-10 print:static">
                          <td className="sticky left-0 bg-slate-700 p-2 text-right text-[10px] uppercase tracking-wider border-r border-slate-600">Total Guests (AM)</td>
                          {occAmTotals.map((total, idx) => (
                              <td key={`am-${idx}`} className="text-center py-2 border-r border-slate-600 text-sm">{total > 0 ? total : <span className="text-slate-400">-</span>}</td>
                          ))}
                      </tr>
                      {!isKitchenMode && (
                        <tr className="bg-slate-600 text-emerald-300 font-bold sticky bottom-0 z-10 print:static">
                            <td className="sticky left-0 bg-slate-600 p-2 text-right text-[10px] uppercase tracking-wider border-r border-slate-500">Non-Riders (AM)</td>
                            {occAmNonRiders.map((total, idx) => (
                                <td key={`am-nr-${idx}`} className="text-center py-2 border-r border-slate-500 text-sm">{total > 0 ? total : <span className="text-slate-400 opacity-50">-</span>}</td>
                            ))}
                        </tr>
                      )}
                      <tr className="bg-slate-800 text-white font-bold sticky bottom-0 z-10 print:static">
                          <td className="sticky left-0 bg-slate-800 p-2 text-right text-[10px] uppercase tracking-wider border-r border-slate-700">Total Guests (PM)</td>
                          {occPmTotals.map((total, idx) => (
                              <td key={`pm-${idx}`} className="text-center py-2 border-r border-slate-700 text-sm">{total > 0 ? total : <span className="text-slate-500">-</span>}</td>
                          ))}
                      </tr>
                      {!isKitchenMode && (
                        <tr className="bg-slate-700 text-emerald-300 font-bold sticky bottom-0 z-10 print:static">
                            <td className="sticky left-0 bg-slate-700 p-2 text-right text-[10px] uppercase tracking-wider border-r border-slate-600">Non-Riders (PM)</td>
                            {occPmNonRiders.map((total, idx) => (
                                <td key={`pm-nr-${idx}`} className="text-center py-2 border-r border-slate-600 text-sm">{total > 0 ? total : <span className="text-slate-500 opacity-50">-</span>}</td>
                            ))}
                        </tr>
                      )}
                  </tbody>
              </table>
          </div>
          
          {/* Dietary Appendix for Kitchen Calendar */}
          {isKitchenMode && dietaryAppendix.length > 0 && (
              <div className="mt-8 p-6 bg-white border-t border-slate-200 print:break-before-page">
                  <h3 className="text-2xl font-bold text-orange-600 mb-4 border-b border-slate-200 pb-2">Dietary Requirements Appendix</h3>
                  <div className="space-y-3">
                      {dietaryAppendix.map(note => (
                          <div key={note.refId} className="flex gap-4 items-start p-3 bg-orange-50/50 rounded-lg border border-orange-100 break-inside-avoid">
                              <div className="bg-orange-600 text-white font-bold w-8 h-8 rounded flex items-center justify-center shrink-0 mt-1">
                                  {note.refId}
                              </div>
                              <div className="flex-1">
                                  <div className="text-sm text-slate-500 font-bold mb-1">
                                      {note.room} <span className="mx-2">|</span> Booking: {note.bookingName}
                                  </div>
                                  <div className="text-slate-800 mb-2">
                                      <strong className="text-orange-900">{note.guestName} (EN):</strong> {note.details}
                                  </div>
                                  <div className="text-slate-700 italic border-l-2 border-orange-300 pl-2">
                                      <strong className="text-orange-800">ES:</strong> {translations[note.refId] || <span className="text-slate-400 text-xs flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin"/> Translating...</span>}
                                  </div>
                              </div>
                          </div>
                      ))}
                  </div>
              </div>
          )}
      </div>
    </div>
  );
};

const TipsView = ({ tipsMonth, setTipsMonth, tipsPool, setTipsPool, calculateTips, isCalculatingTips, tipsCalculation }) => (
  <div className="bg-white p-6 rounded-xl shadow-lg border border-stone-200 max-w-4xl mx-auto tips-view animate-fade-in w-full">
    <h2 className="text-xl font-bold text-stone-800 mb-6 flex items-center gap-2 no-print"><Coins className="w-5 h-5 text-yellow-500" /> Monthly Tips Calculator</h2>
    
    <div className="flex flex-col md:flex-row gap-6 mb-8 bg-stone-50 p-4 rounded-xl border border-stone-200 no-print">
        <div>
            <label className="block text-sm font-bold text-stone-600 mb-2 uppercase">Select Month</label>
            <input type="month" value={tipsMonth} onChange={(e) => setTipsMonth(e.target.value)} className="w-full md:w-48 border border-stone-300 rounded px-3 py-2 focus:ring-2 focus:ring-cyan-500 outline-none" />
        </div>
        <div>
            <label className="block text-sm font-bold text-stone-600 mb-2 uppercase">Total Tips Pool</label>
            <div className="relative">
                <span className="absolute left-3 top-2.5 text-stone-500 font-bold">€</span>
                <input type="number" value={tipsPool} onChange={(e) => setTipsPool(e.target.value)} placeholder="0.00" className="w-full md:w-48 border border-stone-300 rounded pl-8 pr-3 py-2 focus:ring-2 focus:ring-cyan-500 outline-none" />
            </div>
        </div>
        <div className="flex items-end">
            <button onClick={calculateTips} disabled={isCalculatingTips || !tipsPool} className="w-full md:w-auto px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-bold rounded-lg shadow-sm transition disabled:opacity-50 flex items-center gap-2">
                {isCalculatingTips ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />} Calculate Tips
            </button>
        </div>
    </div>

    {tipsCalculation && (
        <div className="animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-cyan-50 border border-cyan-100 p-4 rounded-xl print:border-slate-300">
                    <div className="text-cyan-800 text-sm font-bold uppercase mb-1">Total Pool</div>
                    <div className="text-2xl font-bold text-cyan-900">€{parseFloat(tipsPool).toFixed(2)}</div>
                </div>
                <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl print:border-slate-300">
                    <div className="text-blue-800 text-sm font-bold uppercase mb-1">Total Shares (Days)</div>
                    <div className="text-2xl font-bold text-blue-900">{tipsCalculation.totalAdjustedDays}</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-100 p-4 rounded-xl print:border-slate-300">
                    <div className="text-emerald-800 text-sm font-bold uppercase mb-1">Value Per Share</div>
                    <div className="text-2xl font-bold text-emerald-900">€{tipsCalculation.valuePerDay.toFixed(2)}</div>
                </div>
            </div>
            <div className="overflow-x-auto rounded-lg border border-stone-200">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-stone-50 border-b border-stone-200 text-xs uppercase tracking-wider text-stone-500 font-bold">
                        <tr>
                            <th className="p-3">Employee</th>
                            <th className="p-3 text-center">Days Worked</th>
                            <th className="p-3 text-center">Multiplier</th>
                            <th className="p-3 text-center bg-blue-50/50">Adjusted Shares</th>
                            <th className="p-3 text-right bg-emerald-50/50">Tip Amount</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100 text-sm">
                        {tipsCalculation.staffResults.map(res => (
                            <tr key={res.id} className="hover:bg-stone-50">
                                <td className="p-3 font-bold text-stone-800">{res.name}</td>
                                <td className="p-3 text-center">{res.workedDays}</td>
                                <td className="p-3 text-center">
                                    <span className={`px-2 py-1 rounded text-xs font-bold ${res.multiplier === 1 ? 'bg-stone-100 text-stone-600' : res.multiplier === 0 ? 'bg-red-100 text-red-600' : 'bg-yellow-100 text-yellow-700'}`}>{res.multiplier}x</span>
                                </td>
                                <td className="p-3 text-center font-bold text-blue-700 bg-blue-50/30">{res.adjustedDays}</td>
                                <td className="p-3 text-right font-bold text-emerald-700 bg-emerald-50/30 text-lg">€{res.tipAmount.toFixed(2)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )}
  </div>
);

const StaffView = ({ staff, holidayRequests, handleUpdateStaffProps, openHolidayModal, setStaff, deleteHolidayRequest }) => {
  const currentYear = new Date().getFullYear();
  const defaultStart = new Date(currentYear, 0, 1).toISOString().split('T')[0];

  const getEmployeeHolidayMetrics = (emp) => {
      const calcD = new Date();
      const startD = new Date(emp.startDate || defaultStart);
      const annualDays = emp.annualDays || 30;
      
      if (isNaN(calcD) || isNaN(startD)) return { remaining: 0, accrued: 0, balance: 0, taken: 0 };

      const taken = holidayRequests.filter(r => r.staffId === emp.id).reduce((sum, r) => sum + r.days, 0);
      const remaining = annualDays - taken;

      if (startD > calcD) return { remaining, accrued: 0, balance: -taken, taken };

      const startOfYear = new Date(currentYear, 0, 1);
      const endOfYear = new Date(currentYear, 11, 31);
      const msInDay = 1000 * 60 * 60 * 24;
      const daysInYear = Math.ceil((endOfYear - startOfYear) / msInDay) + 1;
      
      let effectiveStart = startD < startOfYear ? startOfYear : startD;
      let daysPassed = Math.floor((calcD - effectiveStart) / msInDay) + 1;
      if (daysPassed < 0) daysPassed = 0;

      let accrued = (daysPassed / daysInYear) * annualDays;
      accrued = Math.round(accrued * 10) / 10;
      const balance = Math.round((accrued - taken) * 10) / 10;

      return { remaining, accrued, balance, taken };
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-lg border border-stone-200 max-w-4xl mx-auto no-print animate-fade-in w-full pb-24">
      <h2 className="text-xl font-bold text-stone-800 mb-6 flex items-center gap-2"><Users className="w-5 h-5 text-cyan-600" /> Team Management</h2>
      
      <div className="grid grid-cols-1 gap-4 mb-8">
        {staff.map(member => {
            const metrics = getEmployeeHolidayMetrics(member);
            const isArchived = member.endDate && new Date(member.endDate) < new Date();
            return (
              <div key={member.id} className={`flex flex-col border rounded-lg hover:shadow-sm transition-shadow overflow-hidden group ${isArchived ? 'border-red-200 bg-red-50/30' : 'border-stone-100'}`}>
                  <div className="p-3 bg-white flex flex-col sm:flex-row items-center justify-between gap-3">
                      <div className="flex items-center gap-4 w-full sm:w-auto">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold shrink-0 ${isArchived ? 'bg-red-100 text-red-700' : 'bg-cyan-100 text-cyan-700'}`}>{member.name.substring(0, 1)}</div>
                          <div>
                              <h3 className="font-semibold text-stone-800">{member.name} {isArchived && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-600 px-2 py-0.5 rounded">Archived</span>}</h3>
                              <div className="text-xs text-stone-500 mt-1 flex items-center gap-2">
                                  <span>Starts: <input type="date" value={member.startDate || defaultStart} onChange={(e) => handleUpdateStaffProps(member.id, 'startDate', e.target.value)} className="bg-transparent border-b border-stone-200 outline-none w-[105px]" /></span>
                                  <span className="text-stone-300">|</span>
                                  <span>Ends: <input type="date" value={member.endDate || ''} onChange={(e) => handleUpdateStaffProps(member.id, 'endDate', e.target.value)} className="bg-transparent border-b border-stone-200 outline-none w-[105px]" /></span>
                              </div>
                          </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                          <select value={member.role} onChange={(e) => handleUpdateStaffProps(member.id, 'role', e.target.value)} className="flex-1 sm:w-32 px-2 py-1.5 bg-stone-50 border border-stone-200 rounded text-sm focus:ring-2 focus:ring-cyan-500 outline-none">
                              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                          <select value={member.tipsMultiplier !== undefined ? member.tipsMultiplier : (member.name.toLowerCase() === 'donna' ? 0.5 : (member.name.toLowerCase() === 'javi' ? 0 : 1))} onChange={(e) => handleUpdateStaffProps(member.id, 'tipsMultiplier', parseFloat(e.target.value))} className="flex-1 sm:w-28 px-2 py-1.5 bg-stone-50 border border-stone-200 rounded text-sm focus:ring-2 focus:ring-cyan-500 outline-none">
                              <option value={1}>Tips (1x)</option>
                              <option value={0.5}>Tips (0.5x)</option>
                              <option value={0}>Tips (0x)</option>
                          </select>
                          <button onClick={() => openHolidayModal(member)} className="px-3 py-1.5 bg-purple-100 text-purple-700 hover:bg-purple-200 rounded text-sm font-medium transition flex items-center gap-1 shrink-0"><Palmtree className="w-4 h-4"/> Hol</button>
                          <button onClick={() => handleUpdateStaffProps(member.id, 'showOnPrint', member.showOnPrint === false ? false : true)} className={`p-1.5 transition-colors rounded shrink-0 ${member.showOnPrint !== false ? 'text-stone-400 hover:text-cyan-600 bg-stone-50 hover:bg-cyan-50' : 'text-stone-400 hover:text-stone-600 bg-stone-200 hover:bg-stone-300'}`} title={member.showOnPrint !== false ? "Visible on Print" : "Hidden on Print"}>
                              {member.showOnPrint !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </button>
                          <button onClick={() => setStaff(staff.filter(s => s.id !== member.id))} className="p-1.5 text-stone-400 hover:text-red-500 transition-colors bg-stone-50 hover:bg-red-50 rounded shrink-0"><Trash2 className="w-4 h-4" /></button>
                      </div>
                  </div>
                  <div className="bg-stone-50 p-3 border-t border-stone-100 text-sm">
                      <div className="flex justify-between items-center mb-1">
                          <div className="text-stone-500 font-medium text-xs">Holiday Balance: <span className={`font-bold ${metrics.balance < 0 ? 'text-red-500' : 'text-emerald-600'}`}>{metrics.balance} days</span></div>
                      </div>
                      {holidayRequests.filter(r => r.staffId === member.id).length > 0 && (
                          <div className="space-y-1 mt-2">
                              {holidayRequests.filter(r => r.staffId === member.id).map(req => (
                                  <div key={req.id} className="flex justify-between items-center bg-white border border-stone-200 p-2 rounded text-xs group/req">
                                      <span>{new Date(req.start).toLocaleDateString()} - {new Date(req.end).toLocaleDateString()} <span className="font-bold ml-1">({req.days} d)</span></span>
                                      <button onClick={() => deleteHolidayRequest(req.id)} className="text-stone-400 hover:text-red-500 opacity-0 group-hover/req:opacity-100 transition"><Trash2 className="w-3 h-3" /></button>
                                  </div>
                              ))}
                          </div>
                      )}
                  </div>
              </div>
            );
        })}
      </div>

      <div className="mt-8 pt-6 border-t border-stone-100">
        <h3 className="text-sm font-bold text-stone-400 uppercase tracking-wider mb-4">Add New Employee</h3>
        <form className="flex flex-col md:flex-row gap-3" onSubmit={(e) => { e.preventDefault(); const formData = new FormData(e.target); setStaff([...staff, { id: Date.now(), name: formData.get('name'), role: formData.get('role'), tipsMultiplier: 1, startDate: new Date().toISOString().split('T')[0], annualDays: 30 }]); e.target.reset(); }}>
          <input name="name" required placeholder="Employee Name" className="flex-1 px-4 py-2 rounded-lg border border-stone-300 focus:outline-none focus:ring-2 focus:ring-cyan-500" />
          <select name="role" className="px-4 py-2 rounded-lg border border-stone-300 focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white">
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <button type="submit" className="px-6 py-2 bg-cyan-600 text-white font-medium rounded-lg hover:bg-cyan-700 transition flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Add</button>
        </form>
      </div>
    </div>
  );
};

const DuplicatesModal = ({ showDuplicatesModal, setShowDuplicatesModal, duplicatesList, deleteDuplicateRecord, handleBulkDeleteDuplicates }) => {
  if (!showDuplicatesModal) return null;
  return (
    <div className="fixed inset-0 bg-[#162635]/80 backdrop-blur-sm z-[100] flex justify-center items-center p-4 md:p-8 animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-full overflow-y-auto shadow-2xl relative">
        <button onClick={() => setShowDuplicatesModal(false)} className="absolute top-6 right-6 p-2 text-stone-400 hover:text-rose-600 bg-stone-100 hover:bg-rose-50 rounded-full transition-colors z-10"><X size={20} /></button>
        <div className="p-8 md:p-10">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div>
              <h2 className="text-4xl font-serif font-bold text-[#162635] mb-2">Duplicate Records</h2>
              <p className="text-stone-500">{duplicatesList.length === 0 ? "No duplicates found." : `Found ${duplicatesList.length} grouping(s) of duplicates.`}</p>
            </div>
            {duplicatesList.length > 0 && <button onClick={handleBulkDeleteDuplicates} className="px-5 py-2.5 bg-rose-100 hover:bg-rose-200 text-rose-700 rounded-xl text-sm font-bold transition-all flex items-center gap-2"><Trash2 size={18} /> Delete All Extras</button>}
          </div>
          <div className="space-y-8">
            {duplicatesList.map((group, groupIdx) => (
              <div key={groupIdx} className="bg-[#F0F8F8] border border-stone-200 rounded-2xl overflow-hidden shadow-sm">
                <div className="bg-[#162635] px-6 py-4 border-b border-stone-200 font-bold text-white text-sm flex gap-3"><Users size={18} className="text-[#28CFD4]" /> {group[0].name} — {formatUKDate(group[0].arrivalDate)}</div>
                <div className="divide-y divide-stone-100">
                  {group.map((guest, idx) => (
                    <div key={guest.id} className="p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-white">
                      <div>
                        <div className="font-bold text-[#162635] flex items-center gap-2 mb-1">{guest.name} {idx === 0 && <span className="px-2 py-0.5 bg-[#28CFD4]/10 text-[#28CFD4] text-[10px] uppercase rounded-md font-bold">Suggested Keeper</span>}</div>
                        <div className="text-sm text-stone-500"><span className="inline-block w-20 font-bold">Email:</span> {guest.email || 'N/A'}</div>
                        <div className="text-sm text-stone-500"><span className="inline-block w-20 font-bold">Record ID:</span> <span className="font-mono text-xs">{guest.id}</span></div>
                      </div>
                      <button onClick={() => deleteDuplicateRecord(guest.id)} className="px-4 py-2.5 bg-white border border-rose-200 hover:bg-rose-50 text-rose-600 rounded-xl text-sm font-bold transition-all flex items-center gap-2"><Trash2 size={18} /> Delete</button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {duplicatesList.length === 0 && <div className="flex justify-center pt-8"><button onClick={() => setShowDuplicatesModal(false)} className="px-8 py-3 bg-[#162635] text-white rounded-full font-bold">Close Scanner</button></div>}
        </div>
      </div>
    </div>
  );
};

const GuestDetailsModal = ({ guest, onClose, getGuestDoc, showAlert }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (guest) setEditForm(guest);
    setIsEditing(false);
  }, [guest]);

  if (!guest) return null;
  const abilities = getAbilitiesList(guest);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateDoc(getGuestDoc(guest.id), editForm);
      setIsEditing(false);
      showAlert("Guest details updated successfully!", "Success");
    } catch (error) {
      console.error(error);
      showAlert("Failed to save changes.", "Error");
    }
    setIsSaving(false);
  };

  const renderField = (label, name, type = "text") => {
    if (isEditing) {
      return (
        <div className="mb-2 w-full">
          <label className="block text-[10px] font-bold text-[#28CFD4] uppercase tracking-wider mb-1">{label}</label>
          <input type={type} name={name} value={editForm[name] || ''} onChange={handleChange} className="w-full p-2 border border-stone-300 rounded-lg text-sm outline-none focus:border-[#28CFD4] focus:ring-1 focus:ring-[#28CFD4] transition" />
        </div>
      );
    }
    return <p className="mb-1"><strong className="text-stone-500 inline-block min-w-[100px]">{label}:</strong> <span className="font-medium text-stone-800">{guest[name] || 'N/A'}</span></p>;
  };
  
  return (
    <div className="fixed inset-0 bg-[#162635]/80 backdrop-blur-sm print:static print:block print:bg-white print:backdrop-blur-none z-[100] flex justify-center items-center p-4 md:p-8 print:p-0 animate-in fade-in duration-200">
      <style type="text/css" media="print">
        {`@page { size: A4 portrait; margin: 10mm; }`}
      </style>

      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-full overflow-y-auto shadow-2xl relative print:shadow-none print:w-full print:max-w-none print:h-auto print:max-h-none print:overflow-visible print:rounded-none">
        
        <div className="absolute top-6 right-6 flex items-center gap-2 print:hidden z-10">
          {!isEditing ? (
            <>
              <button onClick={() => setIsEditing(true)} className="px-4 py-2 text-white bg-[#162635] hover:bg-[#28CFD4] rounded-full transition-colors flex items-center gap-2 font-bold text-sm shadow-sm" title="Edit Form">
                <Edit size={16} /> Edit Details
              </button>
              <button onClick={() => window.print()} className="px-4 py-2 text-stone-600 hover:text-[#28CFD4] bg-stone-100 hover:bg-[#F0F8F8] rounded-full transition-colors flex items-center gap-2 font-bold text-sm shadow-sm" title="Print Details">
                <Printer size={16} /> Print Form
              </button>
              <button onClick={onClose} className="p-2 text-stone-400 hover:text-rose-600 bg-stone-100 hover:bg-rose-50 rounded-full transition-colors" title="Close Details">
                <X size={20} />
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setIsEditing(false)} className="px-4 py-2 text-stone-600 hover:text-stone-800 bg-stone-100 hover:bg-stone-200 rounded-full transition-colors font-bold text-sm shadow-sm">
                Cancel
              </button>
              <button onClick={handleSave} disabled={isSaving} className="px-4 py-2 text-white bg-emerald-600 hover:bg-emerald-700 rounded-full transition-colors flex items-center gap-2 font-bold text-sm shadow-sm disabled:opacity-50">
                <Save size={16} /> {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </>
          )}
        </div>

        <div className="p-8 md:p-10 print:p-0">
          <div className="mb-8 print:mb-3">
             {isEditing ? (
                <input type="text" name="name" value={editForm.name || ''} onChange={handleChange} className="text-4xl font-serif font-bold text-[#162635] w-full border-b-2 border-[#28CFD4] outline-none mb-2 bg-stone-50 px-2 py-1" />
             ) : (
                <h2 className="text-4xl font-serif font-bold text-[#162635] mb-2 print:text-2xl print:mb-0.5">{guest.name}</h2>
             )}
             
             {isEditing ? (
                <div className="flex items-center gap-2 mt-2">
                   <span className="text-stone-500 font-medium">Booking Holder:</span>
                   <input type="text" name="bookingHolder" value={editForm.bookingHolder || ''} onChange={handleChange} className="border-b border-stone-300 font-bold text-stone-800 outline-none px-1" />
                </div>
             ) : (
                <p className="text-stone-500 font-medium print:text-[11px] print:text-stone-600">Booking Holder: <span className="text-stone-800 font-bold">{guest.bookingHolder || 'N/A'}</span></p>
             )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 print:gap-4">
            <div className="space-y-6 print:space-y-3">
              <section className="print:break-inside-avoid">
                <h3 className="font-bold text-[#28CFD4] mb-3 uppercase tracking-wider text-xs print:text-[10px] print:text-stone-800 print:mb-1.5 border-b border-stone-200 print:pb-1">Contact & Emergency</h3>
                <div className="bg-[#F0F8F8] print:bg-transparent p-5 print:p-0 rounded-xl space-y-2 print:space-y-1 text-sm print:text-[11px] text-[#162635] border border-stone-100 print:border-none">
                  {renderField("Email", "email", "email")}
                  {renderField("Phone", "phone", "tel")}
                  <hr className="border-stone-200 my-3 print:my-1.5" />
                  {renderField("Emergency Contact", "emergencyContact")}
                  {renderField("Emergency Phone", "emergencyPhone", "tel")}
                </div>
              </section>

              <section className="print:break-inside-avoid">
                <h3 className="font-bold text-[#28CFD4] mb-3 uppercase tracking-wider text-xs print:text-[10px] print:text-stone-800 print:mb-1.5 border-b border-stone-200 print:pb-1">Travel Arrangements</h3>
                <div className="bg-[#F0F8F8] print:bg-transparent p-5 print:p-0 rounded-xl space-y-3 print:space-y-1.5 text-sm print:text-[11px] text-[#162635] border border-stone-100 print:border-none">
                  <div className="grid grid-cols-1 gap-2">
                    {renderField("Arrival Date", "arrivalDate", "date")}
                    <div className="flex gap-4">
                        <div className="flex-1">{renderField("Time", "arrivalTime", "time")}</div>
                        <div className="flex-1">{renderField("Flight No", "arrivalFlightNo")}</div>
                    </div>
                  </div>
                  <hr className="border-stone-200 my-2 print:my-1" />
                  <div className="grid grid-cols-1 gap-2">
                    {renderField("Departure Date", "departureDate", "date")}
                    <div className="flex gap-4">
                        <div className="flex-1">{renderField("Time", "departureTime", "time")}</div>
                        <div className="flex-1">{renderField("Flight No", "departureFlightNo")}</div>
                    </div>
                  </div>
                  <hr className="border-stone-200 my-2 print:my-1" />
                  {renderField("Transfers Req.", "transfers")}
                </div>
              </section>
            </div>

            <div className="space-y-6 print:space-y-3">
              <section className="print:break-inside-avoid">
                <h3 className="font-bold text-[#28CFD4] mb-3 uppercase tracking-wider text-xs print:text-[10px] print:text-stone-800 print:mb-1.5 border-b border-stone-200 print:pb-1">Rider Profile</h3>
                <div className="bg-[#F0F8F8] print:bg-transparent p-5 print:p-0 rounded-xl space-y-3 print:space-y-1.5 text-sm print:text-[11px] text-[#162635] border border-stone-100 print:border-none">
                  <div className="flex gap-6 print:gap-4 flex-wrap">
                    {renderField("Age", "age", "number")}
                    {renderField("Height", "height")}
                    {renderField("Weight", "weight")}
                  </div>
                  <hr className="border-stone-200 my-2 print:my-1" />
                  {!isEditing && <p><strong className="text-stone-500 print:text-stone-800 block mb-1 print:mb-0">Abilities:</strong> {abilities.length > 0 ? abilities.join(', ') : 'None specified'}</p>}
                  <div>
                    {!isEditing && <strong className="text-stone-500 print:text-stone-800 block mb-1 print:mb-0">Experience:</strong>}
                    {isEditing ? (
                        <div className="mt-2 w-full"><label className="block text-[10px] font-bold text-[#28CFD4] uppercase tracking-wider mb-1">Experience</label>
                        <textarea name="experience" value={editForm.experience || ''} onChange={handleChange} rows="3" className="w-full p-2 border border-stone-300 rounded-lg text-sm outline-none focus:border-[#28CFD4] transition"></textarea></div>
                    ) : (
                        <div className="italic text-stone-600 print:text-stone-800 bg-white print:bg-transparent p-3 print:p-0 rounded-lg border border-stone-100 print:border-none whitespace-pre-wrap print:leading-tight">{guest.experience || 'None provided'}</div>
                    )}
                  </div>
                </div>
              </section>

              <section className="print:break-inside-avoid">
                <h3 className="font-bold text-[#28CFD4] mb-3 uppercase tracking-wider text-xs print:text-[10px] print:text-stone-800 print:mb-1.5 border-b border-stone-200 print:pb-1">Health & Needs</h3>
                <div className="bg-[#F0F8F8] print:bg-transparent p-5 print:p-0 rounded-xl space-y-3 print:space-y-1 text-sm print:text-[11px] text-[#162635] border border-stone-100 print:border-none">
                  <div className="flex gap-4 items-center">{renderField("Dietary", "hasDietary")} {isEditing && renderField("Details", "dietaryDetails")}</div>
                  {!isEditing && guest.hasDietary === 'Yes' && <p className="text-rose-600 font-bold ml-4 -mt-2">{guest.dietaryDetails}</p>}
                  
                  <div className="flex gap-4 items-center">{renderField("Medical", "hasMedical")} {isEditing && renderField("Details", "medicalDetails")}</div>
                  {!isEditing && guest.hasMedical === 'Yes' && <p className="text-rose-600 font-bold ml-4 -mt-2">{guest.medicalDetails}</p>}
                  
                  <div className="flex gap-4 items-center">{renderField("Addtl Needs", "hasNeeds")} {isEditing && renderField("Details", "needsDetails")}</div>
                  {!isEditing && guest.hasNeeds === 'Yes' && <p className="text-amber-600 font-bold ml-4 -mt-2">{guest.needsDetails}</p>}
                </div>
              </section>
            </div>
            
            <div className="md:col-span-2 print:mt-2">
              <section className="print:break-inside-avoid">
                <h3 className="font-bold text-[#28CFD4] mb-3 uppercase tracking-wider text-xs print:text-[10px] print:text-stone-800 print:mb-1.5 border-b border-stone-200 print:pb-1">Legal & Consent</h3>
                <div className="bg-[#F0F8F8] print:bg-transparent p-5 print:p-0 rounded-xl grid grid-cols-1 md:grid-cols-2 gap-4 print:gap-8 text-sm print:text-[11px] text-[#162635] border border-stone-100 print:border-none">
                  <div>
                    {isEditing ? renderField("Has Insurance", "hasInsurance") : (
                        <p className="mb-2 print:mb-1"><strong className="text-stone-500 print:text-stone-800">Insurance:</strong> {guest.hasInsurance === 'Yes' ? <span className="font-bold print:px-0">Covered</span> : <span className="font-bold print:px-0">€10/day Cover Selected</span>}</p>
                    )}
                    {(editForm.hasInsurance === 'Yes' || (!isEditing && guest.hasInsurance === 'Yes')) && (
                      <div className="bg-white print:bg-transparent p-3 print:p-0 rounded-lg border border-stone-100 print:border-none mt-2 print:mt-1">
                        {renderField("Company", "insuranceCompany")}
                        {renderField("Policy No", "policyNumber")}
                      </div>
                    )}
                  </div>
                  <div className="space-y-3 print:space-y-1.5 bg-white print:bg-transparent p-4 print:p-0 rounded-lg border border-stone-100 print:border-none">
                    {renderField("Photo Consent", "photoConsent")}
                    {!isEditing && <p><strong className="text-stone-500 print:text-stone-800 inline-block w-32">Terms Accepted:</strong> <span className="font-bold">{guest.termsAccepted ? 'Yes' : 'No'}</span></p>}
                    <hr className="border-stone-100 print:border-stone-300 my-2 print:my-1" />
                    <div className="print:mt-2">
                      <strong className="text-stone-500 print:text-stone-800 block text-xs print:text-[10px] uppercase mb-1">Digital Signature</strong>
                      {isEditing ? renderField("Signature Name", "signatureName") : <p className="font-serif text-lg print:text-base text-[#28CFD4] print:text-stone-900">{guest.signatureName}</p>}
                      {isEditing ? renderField("Date Signed", "dateSigned", "date") : <p className="text-xs print:text-[10px] text-stone-400 print:text-stone-600">Signed on {formatUKDate(guest.dateSigned)}</p>}
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function App() {
  const [user, setUser] = useState(null);
  const [guests, setGuests] = useState([]);
  const [currentView, setCurrentView] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [specificDate, setSpecificDate] = useState(new Date().toISOString().split('T')[0]);
  const [sortBy, setSortBy] = useState('arrivalDate');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  const [duplicatesList, setDuplicatesList] = useState([]);

  const [alertConfig, setAlertConfig] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState(null);
  const showAlert = (message, title = "Notification") => setAlertConfig({ message, title });
  const showConfirm = (message, onConfirm, title = "Confirm") => setConfirmConfig({ message, onConfirm, title });

  const [currentWeekStart, setCurrentWeekStart] = useState(getStartOfWeek(new Date()));
  const [staff, setStaff] = useState(INITIAL_STAFF);
  const [shifts, setShifts] = useState({});
  const [guestCounts, setGuestCounts] = useState({});
  const [isSavingRota, setIsSavingRota] = useState(false);
  const [loadHistory, setLoadHistory] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const [occupancyDate, setOccupancyDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [rooms, setRooms] = useState(KNOWN_ROOMS);
  const [icalUrl, setIcalUrl] = useState('');
  const [isLoadingIcal, setIsLoadingIcal] = useState(false);
  const [isSavingIcal, setIsSavingIcal] = useState(false);
  
  // Modals for Events
  const [editingEvent, setEditingEvent] = useState(null);
  const [overrideEndDate, setOverrideEndDate] = useState('');
  const [showAddBookingModal, setShowAddBookingModal] = useState(false);
  const [newBookingData, setNewBookingData] = useState({ guest: '', room: '', startDate: '', endDate: '', occupancy: 2, isNewRoom: false, newRoomName: '' });

  const [tipsMonth, setTipsMonth] = useState(new Date().toISOString().slice(0, 7));
  const [tipsPool, setTipsPool] = useState('');
  const [tipsCalculation, setTipsCalculation] = useState(null);
  const [isCalculatingTips, setIsCalculatingTips] = useState(false);

  const [holidayRequests, setHolidayRequests] = useState([]);
  const [isHolidayModalOpen, setIsHolidayModalOpen] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [holReqStart, setHolReqStart] = useState('');
  const [holReqEnd, setHolReqEnd] = useState('');
  const [holExcludeWeekends, setHolExcludeWeekends] = useState(true);
  const [holCalculatedDays, setHolCalculatedDays] = useState(0);

  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Lato:wght@400;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/ical.js/1.5.0/ical.min.js';
    script.async = true;
    document.head.appendChild(script);

    setIcalUrl(localStorage.getItem('innstyle_ical_url') || '');
  }, []);

  useEffect(() => {
    let styleEl = document.getElementById('rota-print-styles');
    const isRotaView = ['rota', 'occupancy', 'kitchenCalendar', 'tips', 'staff', 'arrivals'].includes(currentView);
    
    if (isRotaView && !styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = 'rota-print-styles';
        styleEl.innerHTML = `
            @media print {
                @page { size: A4 landscape; margin: 8mm; }
                body { background: white !important; }
                .rota-view, .occupancy-view, .kitchen-calendar-view, .tips-view { zoom: 80% !important; }
                .no-print, .print\\:hidden { display: none !important; }
                .print-container, .print-container div, .overflow-x-auto { 
                    overflow: visible !important; 
                    box-shadow: none !important; border: none !important; margin: 0 !important; 
                }
                table { width: 100% !important; min-width: 100% !important; table-layout: fixed !important; border-collapse: collapse !important; }
                th, td { border: 1px solid #cbd5e1 !important; padding: 4px !important; height: 35px !important; vertical-align: middle !important; }
                ::-webkit-scrollbar { display: none; }
                .page-break { page-break-before: always; }
            }
        `;
        document.head.appendChild(styleEl);
    } else if (!isRotaView && styleEl) {
        styleEl.remove();
    }
  }, [currentView]);

  useEffect(() => {
    const initAuth = async () => {
      try {
        if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) await signInWithCustomToken(authGuest, __initial_auth_token);
        else await signInAnonymously(authGuest);
      } catch (error) { console.error("Guest Auth error:", error); }

      try {
        await signInAnonymously(authRota);
      } catch(error) { console.error("Rota Auth Error", error); }
    };
    initAuth();

    const unsubscribe = onAuthStateChanged(authGuest, (currentUser) => {
      setUser(currentUser);
      if (!currentUser) setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    try {
      const guestsRef = getGuestsCollection();
      const unsubscribe = onSnapshot(guestsRef, (snapshot) => {
        const guestData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setGuests(guestData);
        setLoading(false);
      }, (error) => { console.error("Error fetching guests:", error); setLoading(false); });
      return () => unsubscribe();
    } catch (err) { console.error("Failed to init guest snapshot:", err); setLoading(false); }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const occRef = doc(dbRota, 'artifacts', OCCUPANCY_APP_ID, 'public', 'data', 'calendar', 'main');
    const unsubOcc = onSnapshot(occRef, (docSnap) => {
        if (docSnap.exists()) {
            const data = docSnap.data();
            if (data.events) {
                const processedEvents = data.events.map(ev => ({
                    ...ev,
                    startDate: new Date(ev.startDate),
                    endDate: new Date(ev.endDate),
                    originalEndDate: ev.originalEndDate ? new Date(ev.originalEndDate) : null
                }));
                setEvents(processedEvents);
            }
            if (data.rooms && Array.isArray(data.rooms)) {
                setRooms(data.rooms);
            }
        }
    });

    const holRef = doc(dbRota, 'artifacts', ROTA_APP_ID, 'public', 'data', 'holidays', 'main');
    const unsubHol = onSnapshot(holRef, (docSnap) => {
        if (docSnap.exists()) setHolidayRequests(docSnap.data().requests || []);
    });

    return () => { unsubOcc(); unsubHol(); };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const fetchStaff = async () => {
      try {
        const staffRef = doc(dbRota, 'artifacts', ROTA_APP_ID, 'public', 'data', 'staff', 'main');
        const snap = await getDoc(staffRef);
        if (snap.exists()) setStaff(snap.data().list || INITIAL_STAFF);
        else await setDoc(staffRef, { list: INITIAL_STAFF });
      } catch (err) { console.error("Error fetching staff:", err); }
    };
    fetchStaff();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const fetchWeek = async () => {
      const weekId = formatDateKey(currentWeekStart);
      const weekRef = doc(dbRota, 'artifacts', ROTA_APP_ID, 'public', 'data', 'rotas', weekId);
      try {
        const snap = await getDoc(weekRef);
        if (snap.exists()) {
          const data = snap.data();
          setShifts(prev => ({ ...prev, ...data.shifts }));
          setGuestCounts(prev => ({ ...prev, ...data.guestCounts }));
        } else {
            setShifts({}); setGuestCounts({});
        }
      } catch (err) { console.error("Error fetching week:", err); }
    };
    fetchWeek();
  }, [user, currentWeekStart]);

  const handleCopyEmails = async () => {
    const uniqueGuests = [];
    const seenEmails = new Set();
    displayGuests.forEach(g => {
      const email = (g.email || '').trim().toLowerCase();
      if (email && !seenEmails.has(email)) {
        seenEmails.add(email); uniqueGuests.push(g);
      }
    });
    if (uniqueGuests.length === 0) { showAlert("No valid email addresses found."); return; }
    const formattedContacts = uniqueGuests.map(g => `${g.name || 'Guest'} <${g.email.trim()}>`).join(',\n');
    try {
      await navigator.clipboard.writeText(formattedContacts);
      showAlert(`Successfully copied ${uniqueGuests.length} contacts to your clipboard!`);
    } catch (err) { showAlert("Please copy the contacts manually (clipboard access denied).", "Warning"); }
  };

  const handleFindDuplicates = () => {
    const grouped = {};
    guests.forEach(g => {
      const key = `${(g.name || '').toLowerCase().trim()}|${(g.email || '').toLowerCase().trim()}|${g.arrivalDate || ''}`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(g);
    });
    setDuplicatesList(Object.values(grouped).filter(group => group.length > 1));
    setShowDuplicatesModal(true);
  };

  const deleteDuplicateRecord = async (id) => {
    showConfirm('Delete this duplicate record?', async () => {
      try {
        await deleteDoc(getGuestDoc(id));
        setDuplicatesList(prev => {
          const newList = prev.map(group => group.filter(g => g.id !== id)).filter(group => group.length > 1);
          if (newList.length === 0) setTimeout(() => setShowDuplicatesModal(false), 500);
          return newList;
        });
      } catch (err) { console.error("Delete failed:", err); }
    });
  };

  const handleBulkDeleteDuplicates = async () => {
    const idsToDelete = duplicatesList.flatMap(group => group.slice(1).map(g => g.id));
    if (idsToDelete.length === 0) return;
    showConfirm(`Delete ALL ${idsToDelete.length} extra records?`, async () => {
      try {
        await Promise.all(idsToDelete.map(id => deleteDoc(getGuestDoc(id))));
        setDuplicatesList([]); setShowDuplicatesModal(false);
      } catch (err) { console.error("Bulk delete failed:", err); }
    });
  };

  const handleDelete = async (id) => {
    showConfirm('Are you sure you want to delete this guest record?', async () => {
      try { await deleteDoc(getGuestDoc(id)); } catch (err) { console.error("Delete failed:", err); }
    });
  };

  const updateEventStatus = (eventId, updates) => {
    const updatedEvents = events.map(ev => ev.id === eventId ? { ...ev, ...updates } : ev);
    setEvents(updatedEvents);
    saveIcalToCloud(updatedEvents, rooms);
  };

  const handlePrevWeek = () => setCurrentWeekStart(addDays(currentWeekStart, -7));
  const handleNextWeek = () => setCurrentWeekStart(addDays(currentWeekStart, 7));
  const weekDates = useMemo(() => {
    const dates = [];
    for (let i = 0; i < 7; i++) dates.push(addDays(currentWeekStart, i));
    return dates;
  }, [currentWeekStart]);

  const syncGuestsFromBookings = () => {
    if (!events || events.length === 0) { showAlert("No occupancy events loaded. Please ensure you have imported an iCal feed.", "Warning"); return; }
    const newCounts = { ...guestCounts };
    weekDates.forEach(date => {
        const dKey = formatDateKey(date);
        const targetDayStart = new Date(date).setHours(0,0,0,0);
        const yesterdayStart = new Date(addDays(date, -1)).setHours(0,0,0,0);
        let pmTotal = 0; let amTotal = 0;
        events.forEach(ev => {
            const evStart = new Date(ev.startDate).setHours(0,0,0,0);
            const evEnd = new Date(ev.endDate).setHours(0,0,0,0);
            if (targetDayStart >= evStart && targetDayStart < evEnd) pmTotal += ev.occupancy;
            if (yesterdayStart >= evStart && yesterdayStart < evEnd) amTotal += ev.occupancy;
        });
        newCounts[dKey] = { am: amTotal, pm: pmTotal };
    });
    setGuestCounts(newCounts);
    showAlert("Guest numbers successfully synced from Occupancy calendar! AM/PM carryovers applied.");
  };

  const handleSaveRota = async () => {
    if (!user) return;
    setIsSavingRota(true);
    try {
      const weekId = formatDateKey(currentWeekStart);
      await setDoc(doc(dbRota, 'artifacts', ROTA_APP_ID, 'public', 'data', 'staff', 'main'), { list: staff });
      const weekEnd = addDays(currentWeekStart, 7);
      const weekShifts = {}; const weekGuests = {};
      Object.keys(shifts).forEach(key => {
        const datePart = key.split('_')[0];
        const shiftDate = new Date(datePart);
        if (shiftDate >= currentWeekStart && shiftDate < weekEnd) weekShifts[key] = shifts[key];
      });
      Object.keys(guestCounts).forEach(key => {
        const date = new Date(key);
        if (date >= currentWeekStart && date < weekEnd) weekGuests[key] = guestCounts[key];
      });
      await setDoc(doc(dbRota, 'artifacts', ROTA_APP_ID, 'public', 'data', 'rotas', weekId), {
        startDate: weekId, shifts: weekShifts, guestCounts: weekGuests, lastUpdated: new Date().toISOString()
      });
      showAlert('Rota saved successfully!', 'Success');
    } catch (err) { console.error("Error saving rota:", err); } finally { setIsSavingRota(false); }
  };

  const loadPastRotas = async () => {
    if (!user) return;
    setShowHistoryModal(true);
    try {
      const snap = await getDocs(collection(dbRota, 'artifacts', ROTA_APP_ID, 'public', 'data', 'rotas'));
      const foundWeeks = snap.docs.map(d => d.data());
      foundWeeks.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
      setLoadHistory(foundWeeks);
    } catch (err) { console.error("Error loading history:", err); }
  };

  const loadSelectedWeek = (dateStr) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      setCurrentWeekStart(getStartOfWeek(new Date(year, month - 1, day, 12, 0, 0)));
      setShowHistoryModal(false);
  };

  const isDateOfficialHoliday = (dateStr, staffId) => holidayRequests.some(req => req.staffId === staffId && dateStr >= req.start && dateStr <= req.end);

  const toggleShift = (dateStr, staffId) => {
    if (isDateOfficialHoliday(dateStr, staffId)) { showAlert("This day is booked as an official holiday.", "Blocked"); return; }
    const key = `${dateStr}_${staffId}`;
    const current = shifts[key];
    let next;
    if (!current) next = 'FULL';
    else if (current === 'FULL') next = 'OFF';
    else if (current === 'OFF') next = 'AM';
    else if (current === 'AM') next = 'PM';
    else if (current === 'PM') next = 'SICK';
    else if (current === 'SICK') next = null;
    setShifts(prev => {
      const nextState = { ...prev };
      if (next) nextState[key] = next; else delete nextState[key];
      return nextState;
    });
  };

  const getShiftIcon = (type, isOfficialHoliday) => {
    if (isOfficialHoliday) return <span className="text-[10px] md:text-xs font-bold text-purple-600 bg-purple-100 px-1 py-0.5 rounded">HOL</span>;
    switch(type) {
      case 'FULL': return <Check className="w-5 h-5 text-cyan-600 font-bold mx-auto" strokeWidth={4} />;
      case 'OFF': return <span className="text-xs font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded">OFF</span>;
      case 'AM': return <span className="text-xs font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">AM</span>;
      case 'PM': return <span className="text-xs font-bold text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded">PM</span>;
      case 'SICK': return <span className="text-[10px] md:text-xs font-bold text-yellow-600 bg-yellow-100 px-1 py-0.5 rounded">SICK</span>;
      default: return null;
    }
  };

  const updateGuestCount = (dateStr, type, delta) => {
    setGuestCounts(prev => {
      const dayData = prev[dateStr] || { am: 0, pm: 0 };
      const newVal = Math.max(0, (dayData[type] || 0) + delta);
      return { ...prev, [dateStr]: { ...dayData, [type]: newVal } };
    });
  };

  const calculateTips = async () => {
      if (!user) return;
      setIsCalculatingTips(true);
      try {
          const snap = await getDocs(collection(dbRota, 'artifacts', ROTA_APP_ID, 'public', 'data', 'rotas'));
          const allShifts = {};
          snap.forEach(doc => { if (doc.data().shifts) Object.assign(allShifts, doc.data().shifts); });
          const [year, month] = tipsMonth.split('-').map(Number);
          const daysInMonth = new Date(year, month, 0).getDate();
          let totalAdjustedDays = 0;
          
          const tipMonthStart = new Date(year, month - 1, 1);
          const activeStaff = staff.filter(s => {
              if (!s.endDate) return true;
              const endD = new Date(s.endDate);
              endD.setHours(23, 59, 59, 999);
              return endD >= tipMonthStart;
          });

          const staffResults = activeStaff.map(emp => {
              let workedDays = 0;
              for (let day = 1; day <= daysInMonth; day++) {
                  const shift = allShifts[`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}_${emp.id}`];
                  if (shift === 'FULL') workedDays += 1;
                  else if (shift === 'AM' || shift === 'PM') workedDays += 0.5;
              }
              let defaultMultiplier = emp.name.toLowerCase() === 'donna' ? 0.5 : (emp.name.toLowerCase() === 'javi' ? 0 : 1);
              let multiplier = emp.tipsMultiplier !== undefined ? parseFloat(emp.tipsMultiplier) : defaultMultiplier;
              const adjustedDays = workedDays * multiplier;
              totalAdjustedDays += adjustedDays;
              return { ...emp, workedDays, multiplier, adjustedDays, tipAmount: 0 };
          });

          const valuePerDay = totalAdjustedDays > 0 ? parseFloat(tipsPool) / totalAdjustedDays : 0;
          staffResults.forEach(res => { res.tipAmount = res.adjustedDays * valuePerDay; });
          staffResults.sort((a, b) => b.tipAmount - a.tipAmount);
          setTipsCalculation({ staffResults: staffResults.filter(s => s.workedDays > 0 || s.multiplier === 0), totalAdjustedDays, valuePerDay });
      } catch (err) { showAlert("Failed to calculate tips.", "Error"); } finally { setIsCalculatingTips(false); }
  };

  const saveIcalToCloud = async (newEvents, newRooms) => {
      if (!user) return;
      setIsSavingIcal(true);
      try {
          const storageEvents = newEvents.map(ev => ({
              ...ev, 
              startDate: ev.startDate.toISOString(), 
              endDate: ev.endDate.toISOString(),
              originalEndDate: ev.originalEndDate ? ev.originalEndDate.toISOString() : null, 
              isManualOverride: ev.isManualOverride || false,
              isManualRoomOverride: ev.isManualRoomOverride || false,
              originalRoom: ev.originalRoom || null,
              balancePaid: ev.balancePaid || false, 
              preArrivalSent: ev.preArrivalSent || false, 
              reminderSent: ev.reminderSent || false, 
              guestEmail: ev.guestEmail || "",
              isManualBooking: ev.isManualBooking || false
          }));
          await setDoc(doc(dbRota, 'artifacts', OCCUPANCY_APP_ID, 'public', 'data', 'calendar', 'main'), { events: storageEvents, rooms: newRooms, lastUpdated: new Date().toISOString() });
      } catch (err) { showAlert("Failed to save Occupancy data.", "Error"); } finally { setIsSavingIcal(false); }
  };

  const processICalData = (icalString) => {
      if (!window.ICAL) { showAlert("Calendar library is loading. Please wait a moment and try again.", "Warning"); return; }
      if (!icalString || typeof icalString !== 'string') throw new Error("Invalid data received");
      try {
          const jcalData = window.ICAL.parse(icalString);
          const comp = new window.ICAL.Component(jcalData);
          const vevents = comp.getAllSubcomponents('vevent');
          const parsedEvents = [];
          
          vevents.forEach(ev => {
              const eventComp = new window.ICAL.Event(ev);
              const summary = eventComp.summary || "";
              const description = eventComp.description || "";
              const status = eventComp.component.getFirstPropertyValue('status');
              
              if (status && (status.toString().toUpperCase() === 'CANCELLED' || status.toString().toUpperCase() === 'CANCELED')) return; 
              if (summary.toUpperCase().includes('CANCELLED') || description.toUpperCase().includes('CANCELLED')) return; 
              
              const { room, guest } = parseSummary(summary);
              const occupancy = parseOccupancy(description, summary);
              
              parsedEvents.push({
                  id: eventComp.uid, room: room, guest: guest, occupancy: occupancy,
                  startDate: eventComp.startDate.toJSDate(), endDate: eventComp.endDate.toJSDate(), 
                  originalEndDate: null, isManualOverride: false,
                  originalRoom: null, isManualRoomOverride: false,
                  balancePaid: false, preArrivalSent: false, reminderSent: false, guestEmail: "",
                  isManualBooking: false
              });
          });
          
          const existingManualBookings = events.filter(e => e.isManualBooking);

          // Merge fresh iCal data with our local manual overrides (Room & Dates)
          const mergedEvents = parsedEvents.map(newEv => {
              const existingEv = events.find(e => e.id === newEv.id);
              if (existingEv) {
                  let mergedRoom = newEv.room;
                  if (existingEv.isManualRoomOverride && existingEv.room) {
                      mergedRoom = existingEv.room; // Keep the manual room override
                  }
                  
                  let mergedEndDate = newEv.endDate;
                  if (existingEv.isManualOverride && existingEv.endDate) {
                      mergedEndDate = existingEv.endDate; // Keep the manual date override
                  }

                  return { 
                      ...newEv, 
                      room: mergedRoom,
                      originalRoom: existingEv.isManualRoomOverride ? newEv.room : null,
                      isManualRoomOverride: existingEv.isManualRoomOverride || false,
                      endDate: mergedEndDate,
                      originalEndDate: existingEv.isManualOverride ? newEv.endDate : null,
                      isManualOverride: existingEv.isManualOverride || false,
                      balancePaid: existingEv.balancePaid, 
                      preArrivalSent: existingEv.preArrivalSent, 
                      reminderSent: existingEv.reminderSent, 
                      guestEmail: existingEv.guestEmail 
                  };
              }
              return newEv;
          });

          const finalEvents = [...mergedEvents, ...existingManualBookings];
          saveIcalToCloud(finalEvents, rooms);
      } catch (err) { throw err; }
  };

  const fetchIcalData = async (url) => {
      if (!url) return;
      setIsLoadingIcal(true);
      const tryFetch = async (proxyUrl, isJson = false) => {
          const response = await fetch(proxyUrl, { cache: 'no-store' });
          if (!response.ok) throw new Error('Network response was not ok');
          if (isJson) {
              const data = await response.json();
              if (!data.contents) throw new Error('No content received');
              return data.contents;
          } else return await response.text();
      };
      try {
          const data1 = await tryFetch(`https://corsproxy.io/?${encodeURIComponent(url)}`, false);
          processICalData(data1);
      } catch (err1) {
          try {
              const data2 = await tryFetch(`https://api.allorigins.win/get?url=${encodeURIComponent(url)}&disableCache=true&t=${Date.now()}`, true);
              processICalData(data2);
          } catch (err2) { console.error("All fetch methods failed"); }
      } finally { setIsLoadingIcal(false); }
  };

  const handleFileUpload = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
          try { processICalData(e.target.result); showAlert("Calendar data imported!", "Success"); } 
          catch (err) { showAlert("Error importing file. Please check the file format.", "Error"); }
      };
      reader.readAsText(file);
      e.target.value = null;
  };

  const handleSetUrl = () => {
      const url = window.prompt("Paste your Inn Style iCal URL (starts with https:// and ends in .ics):", icalUrl);
      if (url) {
          setIcalUrl(url); localStorage.setItem('innstyle_ical_url', url); fetchIcalData(url); 
      }
  };

  const openEditEventModal = (event) => { 
      setEditingEvent(event); 
      setOverrideEndDate(formatDateKey(event.endDate)); 
      
      // Always pre-fill this, so even iCal events can have their room changed in the UI
      setNewBookingData({
          guest: event.guest,
          room: event.room,
          startDate: formatDateKey(event.startDate),
          endDate: formatDateKey(event.endDate),
          occupancy: event.occupancy,
          isNewRoom: false,
          newRoomName: ''
      });
  };

  const saveEventOverride = () => {
      if (!editingEvent) return;
      
      let updatedEvents;
      let updatedRooms = [...rooms];

      let targetRoom = newBookingData.room;
      if (newBookingData.isNewRoom && newBookingData.newRoomName) {
          targetRoom = newBookingData.newRoomName;
          if (!updatedRooms.includes(targetRoom)) {
              updatedRooms.push(targetRoom);
          }
      }

      if (editingEvent.isManualBooking) {
          if (!newBookingData.guest || !newBookingData.startDate || !newBookingData.endDate || (!newBookingData.room && !newBookingData.newRoomName)) {
              showAlert("Please fill out all fields for the manual booking.", "Warning");
              return;
          }
          updatedEvents = events.map(ev => {
              if (ev.id === editingEvent.id) {
                  return {
                      ...ev,
                      guest: newBookingData.guest,
                      room: targetRoom,
                      startDate: new Date(newBookingData.startDate + 'T12:00:00'),
                      endDate: new Date(newBookingData.endDate + 'T12:00:00'),
                      occupancy: parseInt(newBookingData.occupancy, 10)
                  };
              }
              return ev;
          });
      } else {
          if (!overrideEndDate) return;
          updatedEvents = events.map(ev => {
              if (ev.id === editingEvent.id) {
                  const origEndDate = ev.originalEndDate || ev.endDate;
                  const origRoom = ev.originalRoom || ev.room;
                  
                  const isRoomChanged = targetRoom !== origRoom;
                  const isDateChanged = overrideEndDate !== formatDateKey(origEndDate);

                  return { 
                      ...ev, 
                      endDate: new Date(overrideEndDate + 'T12:00:00'), 
                      room: targetRoom,
                      isManualOverride: isDateChanged, 
                      originalEndDate: isDateChanged ? origEndDate : null,
                      isManualRoomOverride: isRoomChanged,
                      originalRoom: isRoomChanged ? origRoom : null 
                  };
              }
              return ev;
          });
      }

      setRooms(updatedRooms);
      setEvents(updatedEvents); 
      saveIcalToCloud(updatedEvents, updatedRooms); 
      setEditingEvent(null);
  };

  const removeEventOverride = () => {
      if (!editingEvent) return;
      const updatedEvents = events.map(ev => {
          if (ev.id === editingEvent.id) {
              return { 
                  ...ev, 
                  endDate: ev.originalEndDate || ev.endDate, 
                  room: ev.originalRoom || ev.room,
                  isManualOverride: false, 
                  originalEndDate: null,
                  isManualRoomOverride: false,
                  originalRoom: null
              };
          }
          return ev;
      });
      setEvents(updatedEvents); 
      saveIcalToCloud(updatedEvents, rooms); 
      setEditingEvent(null);
  };

  const deleteManualBooking = () => {
      if (!editingEvent || !editingEvent.isManualBooking) return;
      showConfirm("Are you sure you want to delete this manual booking?", () => {
          const updatedEvents = events.filter(ev => ev.id !== editingEvent.id);
          setEvents(updatedEvents);
          saveIcalToCloud(updatedEvents, rooms);
          setEditingEvent(null);
      });
  };

  const handleSaveManualBooking = () => {
      if (!newBookingData.guest || !newBookingData.startDate || !newBookingData.endDate || (!newBookingData.room && !newBookingData.newRoomName)) {
          showAlert("Please fill out all required fields.", "Warning");
          return;
      }
      let targetRoom = newBookingData.room;
      let updatedRooms = [...rooms];
      if (newBookingData.isNewRoom && newBookingData.newRoomName) {
          targetRoom = newBookingData.newRoomName;
          if (!updatedRooms.includes(targetRoom)) {
              updatedRooms.push(targetRoom);
          }
      }

      const newEvent = {
          id: 'manual_' + Date.now(),
          guest: newBookingData.guest,
          room: targetRoom,
          occupancy: parseInt(newBookingData.occupancy, 10),
          startDate: new Date(newBookingData.startDate + 'T12:00:00'),
          endDate: new Date(newBookingData.endDate + 'T12:00:00'),
          isManualBooking: true,
          balancePaid: false, preArrivalSent: false, reminderSent: false, guestEmail: ""
      };
      
      const updatedEvents = [...events, newEvent];
      setRooms(updatedRooms);
      setEvents(updatedEvents);
      saveIcalToCloud(updatedEvents, updatedRooms);
      setShowAddBookingModal(false);
  };

  const handleUpdateStaffProps = (id, field, value) => {
      const updatedStaff = staff.map(s => s.id === id ? { ...s, [field]: value } : s);
      setStaff(updatedStaff);
      if (user) setDoc(doc(dbRota, 'artifacts', ROTA_APP_ID, 'public', 'data', 'staff', 'main'), { list: updatedStaff });
  };

  const openHolidayModal = (emp) => {
      setSelectedEmp(emp);
      const tmrw = addDays(new Date(), 1);
      setHolReqStart(formatDateKey(tmrw)); setHolReqEnd(formatDateKey(tmrw)); setHolExcludeWeekends(true);
      setIsHolidayModalOpen(true);
  };

  useEffect(() => {
      if (!holReqStart || !holReqEnd) { setHolCalculatedDays(0); return; }
      const start = new Date(holReqStart); const end = new Date(holReqEnd);
      if (end < start) { setHolCalculatedDays(0); return; }
      let count = 0; let cur = new Date(start);
      while (cur <= end) {
          const day = cur.getDay(); const isWeekend = (day === 0 || day === 6);
          if (!(holExcludeWeekends && isWeekend)) count++;
          cur.setDate(cur.getDate() + 1);
      }
      setHolCalculatedDays(count);
  }, [holReqStart, holReqEnd, holExcludeWeekends]);

  const saveHolidayRequest = async () => {
      if (holCalculatedDays <= 0) { showAlert("Invalid date range.", "Warning"); return; }
      const newRequest = { id: Date.now().toString(), staffId: selectedEmp.id, start: holReqStart, end: holReqEnd, days: holCalculatedDays, timestamp: Date.now() };
      const newRequests = [...holidayRequests, newRequest];
      setHolidayRequests(newRequests); setIsHolidayModalOpen(false);
      if (user) await setDoc(doc(dbRota, 'artifacts', ROTA_APP_ID, 'public', 'data', 'holidays', 'main'), { requests: newRequests });
  };

  const deleteHolidayRequest = (reqId) => {
      showConfirm("Cancel this holiday request?", async () => {
          const newRequests = holidayRequests.filter(r => r.id !== reqId);
          setHolidayRequests(newRequests);
          if (user) await setDoc(doc(dbRota, 'artifacts', ROTA_APP_ID, 'public', 'data', 'holidays', 'main'), { requests: newRequests });
      });
  };

  const months = [...new Set([
    ...guests.map(g => g.arrivalDate?.substring(0, 7)).filter(Boolean),
    ...events.map(e => formatDateKey(e.startDate).substring(0, 7)).filter(Boolean)
  ])].sort().reverse();

  const displayGuests = guests.filter(g => {
    const matchName = g.name?.toLowerCase().includes(searchTerm.toLowerCase()) || g.bookingHolder?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
    
    let matchMonth = true;
    if (filterMonth) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (filterMonth === 'today' || filterMonth === 'next7' || filterMonth === 'next30' || filterMonth === 'specific') {
            const arrStr = g.arrivalDate;
            const depStr = g.departureDate;
            const gArr = arrStr ? new Date(arrStr + 'T12:00:00') : null;
            const gDep = depStr ? new Date(depStr + 'T12:00:00') : null;
            
            if (gArr) gArr.setHours(0,0,0,0);
            if (gDep) gDep.setHours(0,0,0,0);

            if (!gArr && !gDep) {
                matchMonth = false; 
            } else {
                let rangeStart = new Date(today);
                let rangeEnd = new Date(today);

                if (filterMonth === 'today') {
                    // stays today
                } else if (filterMonth === 'next7') {
                    rangeEnd.setDate(today.getDate() + 7);
                } else if (filterMonth === 'next30') {
                    rangeEnd.setDate(today.getDate() + 30);
                } else if (filterMonth === 'specific') {
                    if (!specificDate) {
                        // don't filter until picked
                    } else {
                        const spec = new Date(specificDate);
                        spec.setHours(0,0,0,0);
                        rangeStart = spec;
                        rangeEnd = spec;
                    }
                }

                if (filterMonth === 'specific' && !specificDate) {
                    // Do nothing
                } else {
                    if (currentView === 'arrivals') {
                        // Arrivals & Departures View: Show if arriving OR departing in the range
                        matchMonth = (gArr && gArr >= rangeStart && gArr <= rangeEnd) || 
                                     (gDep && gDep >= rangeStart && gDep <= rangeEnd);
                    } else if (currentView === 'assessments' || currentView === 'insurance') {
                        // Assessments / Insurance View: Show ONLY if arriving in the range
                        matchMonth = (gArr && gArr >= rangeStart && gArr <= rangeEnd);
                    } else {
                        // Kitchen / Stables / Dashboard View: Show if ON SITE (overlapping) in the range
                        matchMonth = (gArr && gDep) ? (gArr <= rangeEnd && gDep >= rangeStart) : false;
                        
                        // Fallbacks if one date is missing
                        if (gArr && !gDep) matchMonth = gArr >= rangeStart && gArr <= rangeEnd;
                        if (!gArr && gDep) matchMonth = gDep >= rangeStart && gDep <= rangeEnd;
                    }
                }
            }
        } else {
            matchMonth = g.arrivalDate?.startsWith(filterMonth) || g.departureDate?.startsWith(filterMonth);
        }
    }
    
    return matchName && matchMonth;
  }).sort((a, b) => {
    let valA = sortBy === 'name' ? (a.name || '').toLowerCase() : new Date(a.arrivalDate || 0).getTime();
    let valB = sortBy === 'name' ? (b.name || '').toLowerCase() : new Date(b.arrivalDate || 0).getTime();
    if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
    if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const displayEvents = events.filter(ev => {
    const today = new Date();
    today.setHours(0,0,0,0);
    if (ev.endDate < today) return false; 

    const matchName = (ev.guest || '').toLowerCase().includes(searchTerm.toLowerCase()) || (ev.room || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchMonth = true;
    if (filterMonth) {
        if (filterMonth === 'today' || filterMonth === 'next7' || filterMonth === 'next30' || filterMonth === 'specific') {
            const evStart = new Date(ev.startDate); evStart.setHours(0,0,0,0);
            const evEnd = new Date(ev.endDate); evEnd.setHours(0,0,0,0);
            
            let rangeStart = new Date(today);
            let rangeEnd = new Date(today);

            if (filterMonth === 'today') {
                // today
            } else if (filterMonth === 'next7') {
                rangeEnd.setDate(today.getDate() + 7);
            } else if (filterMonth === 'next30') {
                rangeEnd.setDate(today.getDate() + 30);
            } else if (filterMonth === 'specific') {
                if (!specificDate) { 
                    matchMonth = true; 
                } else {
                    const spec = new Date(specificDate);
                    spec.setHours(0,0,0,0);
                    rangeStart = spec;
                    rangeEnd = spec;
                }
            }
            
            if (filterMonth === 'specific' && !specificDate) {
                // Do nothing
            } else {
                if (currentView === 'arrivals' || currentView === 'tracker') {
                    // Show if arriving or departing in the range
                    matchMonth = (evStart >= rangeStart && evStart <= rangeEnd) || 
                                 (evEnd >= rangeStart && evEnd <= rangeEnd);
                } else {
                    // Show if on site
                    matchMonth = (evStart <= rangeEnd) && (evEnd >= rangeStart);
                }
            }
        } else {
            const evMonth = formatDateKey(ev.startDate).substring(0, 7);
            const evEndMonth = formatDateKey(ev.endDate).substring(0, 7);
            if (evMonth !== filterMonth && evEndMonth !== filterMonth) matchMonth = false;
        }
    }

    return matchName && matchMonth;
  }).sort((a, b) => {
    let valA = sortBy === 'name' ? (a.guest || '').toLowerCase() : new Date(a.startDate || 0).getTime();
    let valB = sortBy === 'name' ? (b.guest || '').toLowerCase() : new Date(b.startDate || 0).getTime();
    if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
    if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const displayRooms = useMemo(() => {
      const eventRooms = events.map(e => e.room).filter(Boolean);
      return [...new Set([...rooms, ...eventRooms])];
  }, [rooms, events]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F0F8F8]"><div className="w-12 h-12 border-4 border-[#28CFD4] border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="flex min-h-screen bg-[#F0F8F8] font-sans print:block print:bg-white">
      <Sidebar currentView={currentView} setCurrentView={setCurrentView} />
      
      <main className={`flex-1 overflow-y-auto p-8 print:p-0 print:overflow-visible ${selectedGuest ? 'print:hidden' : ''}`}>
        <div className="max-w-7xl mx-auto">
          {/* Guest Manager Views */}
          {['dashboard', 'tracker', 'kitchen', 'stables', 'assessments', 'insurance', 'arrivals'].includes(currentView) && (
            <GlobalToolbar currentView={currentView} displayGuestsLength={currentView === 'tracker' ? displayEvents.length : displayGuests.length} searchTerm={searchTerm} setSearchTerm={setSearchTerm} filterMonth={filterMonth} setFilterMonth={setFilterMonth} specificDate={specificDate} setSpecificDate={setSpecificDate} months={months} sortBy={sortBy} setSortBy={setSortBy} sortOrder={sortOrder} setSortOrder={setSortOrder} handleFindDuplicates={handleFindDuplicates} handleCopyEmails={handleCopyEmails} />
          )}
          
          {currentView === 'dashboard' && <DashboardView displayGuests={displayGuests} setSelectedGuest={setSelectedGuest} handleDelete={handleDelete} />}
          {currentView === 'form' && <RegistrationForm setCurrentView={setCurrentView} showAlert={showAlert} />}
          {currentView === 'tracker' && <FormTrackerView events={displayEvents} displayGuests={guests} updateEventStatus={updateEventStatus} showAlert={showAlert} />}
          {currentView === 'arrivals' && <ArrivalsPrintSheet displayGuests={displayGuests} events={events} />}
          {currentView === 'kitchen' && <PrintView type="kitchen" displayGuests={displayGuests} />}
          {currentView === 'stables' && <PrintView type="stables" displayGuests={displayGuests} />}
          {currentView === 'assessments' && <AssessmentPrintSheet displayGuests={displayGuests} />}
          {currentView === 'insurance' && <InsurancePrintSheet displayGuests={displayGuests} />}

          {/* Rota Manager Views */}
          {currentView === 'rota' && <RotaView currentWeekStart={currentWeekStart} handlePrevWeek={handlePrevWeek} handleNextWeek={handleNextWeek} loadPastRotas={loadPastRotas} isSavingRota={isSavingRota} handleSaveRota={handleSaveRota} weekDates={weekDates} staff={staff} syncGuestsFromBookings={syncGuestsFromBookings} guestCounts={guestCounts} updateGuestCount={updateGuestCount} shifts={shifts} toggleShift={toggleShift} isDateOfficialHoliday={isDateOfficialHoliday} getShiftIcon={getShiftIcon} ROLES={ROLES} />}
          {currentView === 'occupancy' && <OccupancyView occupancyDate={occupancyDate} setOccupancyDate={setOccupancyDate} icalUrl={icalUrl} fetchIcalData={fetchIcalData} isLoadingIcal={isLoadingIcal} isSavingIcal={isSavingIcal} handleSetUrl={handleSetUrl} handleFileUpload={handleFileUpload} events={events} rooms={displayRooms} openEditEventModal={openEditEventModal} guests={guests} isKitchenMode={false} openAddBookingModal={() => { setNewBookingData({ guest: '', room: displayRooms[0] || '', startDate: formatDateKey(new Date()), endDate: formatDateKey(addDays(new Date(), 1)), occupancy: 2, isNewRoom: false, newRoomName: '' }); setShowAddBookingModal(true); }} />}
          {currentView === 'kitchenCalendar' && <OccupancyView occupancyDate={occupancyDate} setOccupancyDate={setOccupancyDate} icalUrl={icalUrl} fetchIcalData={fetchIcalData} isLoadingIcal={isLoadingIcal} isSavingIcal={isSavingIcal} handleSetUrl={handleSetUrl} handleFileUpload={handleFileUpload} events={events} rooms={displayRooms} openEditEventModal={openEditEventModal} guests={guests} isKitchenMode={true} openAddBookingModal={() => { setNewBookingData({ guest: '', room: displayRooms[0] || '', startDate: formatDateKey(new Date()), endDate: formatDateKey(addDays(new Date(), 1)), occupancy: 2, isNewRoom: false, newRoomName: '' }); setShowAddBookingModal(true); }} />}
          {currentView === 'tips' && <TipsView tipsMonth={tipsMonth} setTipsMonth={setTipsMonth} tipsPool={tipsPool} setTipsPool={setTipsPool} calculateTips={calculateTips} isCalculatingTips={isCalculatingTips} tipsCalculation={tipsCalculation} />}
          {currentView === 'staff' && <StaffView staff={staff} holidayRequests={holidayRequests} handleUpdateStaffProps={handleUpdateStaffProps} openHolidayModal={openHolidayModal} setStaff={setStaff} deleteHolidayRequest={deleteHolidayRequest} />}
        </div>
      </main>

      {/* Global Modals */}
      <DuplicatesModal showDuplicatesModal={showDuplicatesModal} setShowDuplicatesModal={setShowDuplicatesModal} duplicatesList={duplicatesList} deleteDuplicateRecord={deleteDuplicateRecord} handleBulkDeleteDuplicates={handleBulkDeleteDuplicates} />
      <GuestDetailsModal guest={selectedGuest} onClose={() => setSelectedGuest(null)} getGuestDoc={getGuestDoc} showAlert={showAlert} />

      {/* Rota Modals */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[80vh] flex flex-col animate-in fade-in">
            <div className="p-4 border-b border-stone-100 flex justify-between items-center">
              <h3 className="font-bold text-lg text-stone-800">Saved Rotas</h3>
              <button onClick={() => setShowHistoryModal(false)} className="p-1 hover:bg-stone-100 rounded-full"><X className="w-5 h-5 text-stone-500" /></button>
            </div>
            <div className="overflow-y-auto p-2">
              {loadHistory.length === 0 ? <div className="text-center py-8 text-stone-400">No saved rotas found.</div> : (
                <div className="space-y-2">
                  {loadHistory.map((week) => (
                    <button key={week.startDate} onClick={() => loadSelectedWeek(week.startDate)} className="w-full text-left p-3 rounded-lg hover:bg-cyan-50 border border-transparent hover:border-cyan-100 transition-colors flex items-center justify-between group">
                      <div>
                        <div className="font-semibold text-cyan-800">Week of {new Date(week.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                        <div className="text-xs text-stone-500">Last updated: {new Date(week.lastUpdated).toLocaleString()}</div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-cyan-500" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ADD MANUAL BOOKING MODAL */}
      {showAddBookingModal && (
          <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 animate-in fade-in p-6">
                  <h3 className="text-xl font-bold text-stone-800 mb-4 border-b pb-2 flex items-center gap-2"><PlusCircle className="w-5 h-5 text-purple-600"/> Add Manual Booking</h3>
                  <div className="space-y-4">
                      <div>
                          <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Guest / Booking Name</label>
                          <input type="text" value={newBookingData.guest} onChange={e => setNewBookingData({...newBookingData, guest: e.target.value})} className="w-full border border-stone-300 rounded px-3 py-2 focus:ring-2 focus:ring-purple-500 outline-none"/>
                      </div>
                      <div>
                          <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Room</label>
                          <select value={newBookingData.isNewRoom ? 'NEW_ROOM_OPTION' : newBookingData.room} onChange={e => {
                              if (e.target.value === 'NEW_ROOM_OPTION') {
                                  setNewBookingData({...newBookingData, isNewRoom: true});
                              } else {
                                  setNewBookingData({...newBookingData, isNewRoom: false, room: e.target.value});
                              }
                          }} className="w-full border border-stone-300 rounded px-3 py-2 focus:ring-2 focus:ring-purple-500 outline-none mb-2 bg-white">
                              {displayRooms.map(r => <option key={r} value={r}>{r}</option>)}
                              <option value="NEW_ROOM_OPTION" className="font-bold text-purple-600">+ Add New Room...</option>
                          </select>
                          {newBookingData.isNewRoom && (
                              <input type="text" placeholder="Enter new room name..." value={newBookingData.newRoomName} onChange={e => setNewBookingData({...newBookingData, newRoomName: e.target.value})} className="w-full border border-purple-300 rounded px-3 py-2 focus:ring-2 focus:ring-purple-500 outline-none bg-purple-50"/>
                          )}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                          <div>
                              <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Arrival Date</label>
                              <input type="date" value={newBookingData.startDate} onChange={e => setNewBookingData({...newBookingData, startDate: e.target.value})} className="w-full border border-stone-300 rounded px-3 py-2 focus:ring-2 focus:ring-purple-500 outline-none"/>
                          </div>
                          <div>
                              <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Departure Date</label>
                              <input type="date" value={newBookingData.endDate} onChange={e => setNewBookingData({...newBookingData, endDate: e.target.value})} className="w-full border border-stone-300 rounded px-3 py-2 focus:ring-2 focus:ring-purple-500 outline-none"/>
                          </div>
                      </div>
                      <div>
                          <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Occupancy (Guests)</label>
                          <input type="number" min="1" value={newBookingData.occupancy} onChange={e => setNewBookingData({...newBookingData, occupancy: e.target.value})} className="w-full border border-stone-300 rounded px-3 py-2 focus:ring-2 focus:ring-purple-500 outline-none"/>
                      </div>
                  </div>
                  <div className="mt-6 flex justify-end items-center gap-2">
                      <button onClick={() => setShowAddBookingModal(false)} className="px-4 py-2 text-stone-600 hover:bg-stone-100 rounded font-medium transition text-sm">Cancel</button>
                      <button onClick={handleSaveManualBooking} className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded shadow-sm transition text-sm">Add Booking</button>
                  </div>
              </div>
          </div>
      )}

      {/* EDIT EVENT MODAL */}
      {editingEvent && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 animate-in fade-in p-6">
              <h3 className="text-xl font-bold text-stone-800 mb-4 border-b pb-2 flex items-center gap-2">
                  <Edit className={`w-5 h-5 ${editingEvent.isManualBooking ? 'text-purple-600' : 'text-cyan-600'}`}/> 
                  {editingEvent.isManualBooking ? 'Edit Manual Booking' : 'Edit Booking Details'}
              </h3>
              
              <div className="space-y-4">
                  {editingEvent.isManualBooking ? (
                      <div>
                          <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Guest / Booking Name</label>
                          <input type="text" value={newBookingData.guest} onChange={e => setNewBookingData({...newBookingData, guest: e.target.value})} className="w-full border border-stone-300 rounded px-3 py-2 focus:ring-2 focus:ring-purple-500 outline-none"/>
                      </div>
                  ) : (
                      <div>
                          <label className="block text-xs font-bold text-stone-500 uppercase">Guest</label>
                          <div className="font-medium text-stone-800 mb-2">{editingEvent.guest}</div>
                      </div>
                  )}

                  <div>
                      <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Room</label>
                      <select value={newBookingData.isNewRoom ? 'NEW_ROOM_OPTION' : newBookingData.room} onChange={e => {
                          if (e.target.value === 'NEW_ROOM_OPTION') {
                              setNewBookingData({...newBookingData, isNewRoom: true});
                          } else {
                              setNewBookingData({...newBookingData, isNewRoom: false, room: e.target.value});
                          }
                      }} className={`w-full border border-stone-300 rounded px-3 py-2 focus:ring-2 outline-none mb-2 bg-white ${editingEvent.isManualBooking ? 'focus:ring-purple-500' : 'focus:ring-cyan-500'}`}>
                          {displayRooms.map(r => <option key={r} value={r}>{r}</option>)}
                          <option value="NEW_ROOM_OPTION" className={`font-bold ${editingEvent.isManualBooking ? 'text-purple-600' : 'text-cyan-600'}`}>+ Add New Room...</option>
                      </select>
                      {newBookingData.isNewRoom && (
                          <input type="text" placeholder="Enter new room name..." value={newBookingData.newRoomName} onChange={e => setNewBookingData({...newBookingData, newRoomName: e.target.value})} className={`w-full border rounded px-3 py-2 focus:ring-2 outline-none ${editingEvent.isManualBooking ? 'border-purple-300 focus:ring-purple-500 bg-purple-50' : 'border-cyan-300 focus:ring-cyan-500 bg-cyan-50'}`}/>
                      )}
                  </div>

                  {editingEvent.isManualBooking ? (
                      <>
                          <div className="grid grid-cols-2 gap-4">
                              <div>
                                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Arrival Date</label>
                                  <input type="date" value={newBookingData.startDate} onChange={e => setNewBookingData({...newBookingData, startDate: e.target.value})} className="w-full border border-stone-300 rounded px-3 py-2 focus:ring-2 focus:ring-purple-500 outline-none"/>
                              </div>
                              <div>
                                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Departure Date</label>
                                  <input type="date" value={newBookingData.endDate} onChange={e => setNewBookingData({...newBookingData, endDate: e.target.value})} className="w-full border border-stone-300 rounded px-3 py-2 focus:ring-2 focus:ring-purple-500 outline-none"/>
                              </div>
                          </div>
                          <div>
                              <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Occupancy (Guests)</label>
                              <input type="number" min="1" value={newBookingData.occupancy} onChange={e => setNewBookingData({...newBookingData, occupancy: e.target.value})} className="w-full border border-stone-300 rounded px-3 py-2 focus:ring-2 focus:ring-purple-500 outline-none"/>
                          </div>
                      </>
                  ) : (
                      <div>
                          <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Check-out Date (Editable)</label>
                          <input type="date" value={overrideEndDate} onChange={e=>setOverrideEndDate(e.target.value)} className="w-full border border-stone-300 rounded px-3 py-2 focus:ring-2 focus:ring-cyan-500 outline-none"/>
                      </div>
                  )}
              </div>
              
              <div className="mt-6 flex justify-between items-center border-t border-stone-100 pt-4">
                  {editingEvent.isManualBooking ? (
                      <button onClick={deleteManualBooking} className="text-sm font-bold text-red-600 hover:bg-red-50 px-3 py-2 rounded transition flex items-center gap-1">
                          <Trash2 size={16} /> Delete
                      </button>
                  ) : (
                      editingEvent.isManualOverride || editingEvent.isManualRoomOverride ? <button onClick={removeEventOverride} className="text-sm font-medium text-red-600 hover:bg-red-50 px-3 py-1.5 rounded transition">Revert to iCal</button> : <div></div>
                  )}
                  <div className="flex gap-2">
                      <button onClick={() => setEditingEvent(null)} className="px-3 py-1.5 text-stone-600 hover:bg-stone-100 rounded font-medium transition text-sm">Cancel</button>
                      <button onClick={saveEventOverride} className={`px-4 py-1.5 text-white font-medium rounded shadow-sm transition text-sm ${editingEvent.isManualBooking ? 'bg-purple-600 hover:bg-purple-700' : 'bg-cyan-600 hover:bg-cyan-700'}`}>Save</button>
                  </div>
              </div>
          </div>
        </div>
      )}

      {isHolidayModalOpen && selectedEmp && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 animate-in fade-in p-6">
              <h3 className="text-xl font-bold text-stone-800 mb-4 border-b pb-2 flex items-center gap-2"><Palmtree className="w-5 h-5 text-purple-600"/> Book Holiday</h3>
              <div className="space-y-4">
                  <div>
                      <label className="block text-xs font-bold text-stone-500 uppercase">Employee</label>
                      <div className="font-medium text-stone-800 p-2 bg-stone-50 rounded border border-stone-200 mt-1">{selectedEmp.name}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                      <div>
                          <label className="block text-xs font-bold text-stone-500 uppercase">Start Date</label>
                          <input type="date" value={holReqStart} onChange={e=>setHolReqStart(e.target.value)} className="mt-1 w-full border border-stone-300 rounded px-2 py-1.5 focus:ring-2 focus:ring-purple-500 outline-none text-sm"/>
                      </div>
                      <div>
                          <label className="block text-xs font-bold text-stone-500 uppercase">End Date</label>
                          <input type="date" value={holReqEnd} onChange={e=>setHolReqEnd(e.target.value)} className="mt-1 w-full border border-stone-300 rounded px-2 py-1.5 focus:ring-2 focus:ring-purple-500 outline-none text-sm"/>
                      </div>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                      <input type="checkbox" id="skipWknds" checked={holExcludeWeekends} onChange={e=>setHolExcludeWeekends(e.target.checked)} className="w-4 h-4 text-purple-600 rounded" />
                      <label htmlFor="skipWknds" className="text-sm text-stone-600 select-none">Exclude Weekends (Sat/Sun)</label>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-lg border border-purple-100 flex justify-between items-center">
                      <span className="text-purple-800 font-semibold text-sm">Days to Deduct:</span>
                      <span className="text-xl font-bold text-purple-900">{holCalculatedDays}</span>
                  </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                  <button onClick={() => setIsHolidayModalOpen(false)} className="px-4 py-2 text-stone-600 hover:bg-stone-100 rounded-lg text-sm font-medium transition">Cancel</button>
                  <button onClick={saveHolidayRequest} className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg shadow-sm transition text-sm">Save Holiday</button>
              </div>
          </div>
        </div>
      )}

      {/* Unified Alert & Confirm System */}
      {alertConfig && (
        <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 animate-in fade-in">
                <h3 className="font-bold text-lg text-stone-800 mb-2">{alertConfig.title}</h3>
                <p className="text-stone-600 mb-6">{alertConfig.message}</p>
                <div className="flex justify-end">
                    <button onClick={() => setAlertConfig(null)} className="px-4 py-2 bg-[#162635] text-white font-medium rounded shadow-sm hover:bg-[#28CFD4] transition">OK</button>
                </div>
            </div>
        </div>
      )}
      {confirmConfig && (
        <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 animate-in fade-in">
                <h3 className="font-bold text-lg text-stone-800 mb-2">{confirmConfig.title}</h3>
                <p className="text-stone-600 mb-6">{confirmConfig.message}</p>
                <div className="flex justify-end gap-3">
                    <button onClick={() => setConfirmConfig(null)} className="px-4 py-2 text-stone-600 hover:bg-stone-100 font-medium rounded">Cancel</button>
                    <button onClick={() => { confirmConfig.onConfirm(); setConfirmConfig(null); }} className="px-4 py-2 bg-rose-600 text-white font-medium rounded shadow-sm hover:bg-rose-700">Confirm</button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
}